// src/pages/draft-orders/DraftOrdersPage.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, subMonths, parseISO, formatISO } from "date-fns";
import { toDate } from "date-fns-tz";
import ReactDOM from "react-dom";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import API from "../../services/api";
import { API as ApiUrls } from "../../utils/apiUrls";
import { getDraftOrderStatusName, DraftOrderSourceEnum } from "../../utils/enums";
import { placementErrorMessage } from "../../utils/orderCreationError";
import { formatCarrier, carrierAccent, estDeliveryDate, initialsOf, MODE_STYLE, MODE_ICON } from "../../components/createorder/tokens";
import BookedSuccess from "../../components/createorder/BookedSuccess";

import CustomDateRangePicker from "../../utils/CustomDateRangePicker";
import { toast } from "react-toastify";
import {
  AlertTriangle, ArrowLeft, ArrowRight, Check, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Clock, Copy, Download,
  ExternalLink, Eye, FileSpreadsheet, FileText, IndianRupee, Layers, MapPin, MoreHorizontal, PackageCheck, Pencil, RefreshCw, RotateCcw,
  Send, Sparkles, Trash2, Truck, Upload, Wallet, Warehouse, X, XCircle, Zap,
} from "lucide-react";

let _statusCountsCache = null;
let _statusCountsCacheTime = 0;
const STATUS_COUNTS_CACHE_TTL = 30000; // 30 seconds

/* The placement revamp (OMS #248) added status-counts, pre-flight and placement-run endpoints. An OMS without them
   answers 404; remember that for the session so the fallbacks (five one-row searches, client-side readiness, the
   plain bulk response) cost nothing after the first miss. Any other error only fails the one call. */
const _missingEndpoints = new Set();
/* The shared axios instance (services/api) rejects with a NORMALISED object { message, code, status } — there is no
   `.response`. Read the status from either shape so 404-fallbacks, conflict refetches and the friendly copy work. */
const httpStatus = (err) => err?.status ?? err?.response?.status ?? null;
const errMessage = (err, fallback) => err?.message || err?.response?.data?.message || fallback;
const isNotFound = (err) => httpStatus(err) === 404;
const markMissing = (key, err) => { if (isNotFound(err)) _missingEndpoints.add(key); };

const PREFLIGHT_CHUNK = 50;          // the endpoint's per-call limit
const RUN_POLL_MAX_MS = 15 * 60 * 1000; // stop following a run after 15 minutes; the list still refreshes by hand
const trackingUrl = (awb) => `https://tracking.wayuvega.in/track?awb=${encodeURIComponent(awb)}`;

/* ──────────────── Draft Order Mandatory Field Validation ──────────────── */
/**
 * Validates a draft order's `order_data` against the same mandatory fields
 * required by the Create Forward Order screen.
 * Returns an array of { field, section } objects for each missing/invalid field.
 * An empty array means the draft is valid for placement.
 */
function validateDraftOrderData(orderData) {
  if (!orderData) return [{ field: "Order Data", section: "General", message: "Order data is completely missing" }];

  const missing = [];
  const pickup = orderData.pickup_details || {};
  const drop = orderData.drop_details || {};
  const sd = orderData.shipment_details || {};
  const orderDet = sd.order_details || {};
  const invoiceDet = orderDet.order_invoice_details || {};
  // shipment_packages may be at shipment_details level or order_details level depending on source
  const pkgs = sd.shipment_packages || orderDet.shipment_packages || [];
  const firstPkg = pkgs[0] || {};
  const dims = firstPkg.dimensions || {};
  const weight = firstPkg.weight || {};
  const items = firstPkg.items || [];

  // ── Customer / Delivery Details ──
  if (!drop.drop_poc?.trim()) missing.push({ field: "Customer Name", section: "Delivery" });
  if (!drop.drop_poc_contact_number?.trim() || !/^[0-9]{10}$/.test(drop.drop_poc_contact_number?.trim()))
    missing.push({ field: "Customer Phone (10 digits)", section: "Delivery" });
  if (!drop.drop_address?.trim()) missing.push({ field: "Delivery Address", section: "Delivery" });
  if (!drop.drop_city?.trim()) missing.push({ field: "City", section: "Delivery" });
  if (!drop.drop_state?.trim()) missing.push({ field: "State", section: "Delivery" });
  if (!drop.drop_postal_code?.trim() || !/^[0-9]{6}$/.test(drop.drop_postal_code?.trim()))
    missing.push({ field: "Pincode (6 digits)", section: "Delivery" });

  // ── Pickup Details ──
  if (!pickup.pickup_address?.trim()) missing.push({ field: "Pickup Address", section: "Pickup" });
  if (!pickup.pickup_postal_code?.trim()) missing.push({ field: "Pickup Pincode", section: "Pickup" });

  // ── Order reference: the merchant's own name for the shipment; the desks require it before booking ──
  if (!orderDet.order_reference_number?.trim()) missing.push({ field: "Order Reference", section: "Order" });

  // ── Invoice Details ──
  const invVal = invoiceDet.invoice_value;
  if (invVal == null || invVal === "" || !isFinite(Number(invVal)) || Number(invVal) <= 0)
    missing.push({ field: "Invoice Value", section: "Order" });

  // ── Payment Mode ──
  if (!orderDet.order_type?.trim()) missing.push({ field: "Payment Mode (Prepaid/COD)", section: "Order" });

  // ── Package Dimensions ──
  if (!dims.length || Number(dims.length) <= 0) missing.push({ field: "Length", section: "Package" });
  if (!dims.breadth || Number(dims.breadth) <= 0) missing.push({ field: "Breadth", section: "Package" });
  if (!dims.height || Number(dims.height) <= 0) missing.push({ field: "Height", section: "Package" });

  // ── Package Weight ──
  if (!weight.value || Number(weight.value) <= 0) missing.push({ field: "Weight", section: "Package" });

  // ── Item Details ──
  if (items.length === 0) {
    missing.push({ field: "At least one item required", section: "Items" });
  } else {
    items.forEach((item, idx) => {
      const prefix = items.length > 1 ? `Item ${idx + 1} - ` : "";
      if (!item.item_general_details?.item_name?.trim()) missing.push({ field: `${prefix}Item Name`, section: "Items" });
      if (!item.quantity || Number(item.quantity) <= 0) missing.push({ field: `${prefix}Quantity`, section: "Items" });
    });
  }

  return missing;
}

/* ──────────────────────────── Tabs (5 statuses + All) ──────────────────────────── */
/* Same tokens as the All Shipments strip: icon colour, accent underline, solid badge when active, tint when non-zero. */
const TABS = [
  // "To book" = every draft still waiting to become a shipment (ready or not); "All" = every status together.
  { key: "DRAFT", label: "To book", Icon: FileText, color: "text-[#2A59BD]", accent: "bg-[#3877FC]", badgeSolid: "bg-[#3877FC]", badgeTint: "bg-[#3877FC]/10 text-[#2A59BD] ring-1 ring-[#3877FC]/25" },
  { key: "QUEUED", label: "Queued", Icon: Clock, color: "text-amber-600", accent: "bg-amber-500", badgeSolid: "bg-amber-500", badgeTint: "bg-amber-50 text-amber-700 ring-1 ring-amber-200" },
  { key: "PLACED", label: "Booked", Icon: PackageCheck, color: "text-emerald-600", accent: "bg-emerald-500", badgeSolid: "bg-emerald-500", badgeTint: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" },
  { key: "FAILED", label: "Failed", Icon: XCircle, color: "text-rose-600", accent: "bg-rose-500", badgeSolid: "bg-rose-500", badgeTint: "bg-rose-50 text-rose-700 ring-1 ring-rose-200" },
  { key: "all", label: "All", Icon: Layers, color: "text-slate-600", accent: "bg-slate-500", badgeSolid: "bg-slate-600", badgeTint: "bg-slate-100 text-slate-700 ring-1 ring-slate-200" },
];

/* Deleting a draft removes it for good, in one step — there is no discard/soft-delete. Statuses a merchant may
   delete: not-yet-booked drafts and legacy discarded ones. Never a draft being booked (QUEUED) or a live shipment
   (PLACED); the server refuses those too. */
const DELETABLE_DRAFT_STATUSES = ["DRAFT", "FAILED", "DISCARDED"];
const ACTIONS_BY_STATUS = {
  DRAFT: ["view", "edit", "place", "duplicate", "permanentDelete"],
  QUEUED: ["view", "duplicate"],
  PLACED: ["view", "duplicate", "viewShipment"],
  FAILED: ["view", "edit", "retry", "duplicate", "permanentDelete"],
  DISCARDED: ["view", "duplicate", "permanentDelete"],
};

/* What a live courier quote needs from a draft; empty = the draft can be priced. Short labels, used in the tooltip. */
const quoteBlockersOf = (od) => {
  const out = [];
  const pickup = od?.pickup_details || {};
  const drop = od?.drop_details || {};
  const sd = od?.shipment_details || {};
  const orderDet = sd.order_details || {};
  const firstPkg = (sd.shipment_packages || orderDet.shipment_packages || [])[0] || {};
  const dims = firstPkg.dimensions || {};
  if (!/^[0-9]{6}$/.test(String(pickup.pickup_postal_code || "").trim())) out.push("pickup pincode");
  if (!/^[0-9]{6}$/.test(String(drop.drop_postal_code || "").trim())) out.push("delivery pincode");
  if (!(Number(firstPkg.weight?.value) > 0)) out.push("weight");
  if (!(Number(dims.length) > 0 && Number(dims.breadth) > 0 && Number(dims.height) > 0)) out.push("dimensions");
  if (!String(orderDet.order_type || "").trim()) out.push("payment mode");
  return out;
};
const quoteBlockersLabel = (blockers) => {
  if (!blockers.length) return "";
  const list = blockers.length === 1 ? blockers[0] : `${blockers.slice(0, -1).join(", ")} and ${blockers[blockers.length - 1]}`;
  return `Add ${list} to get prices`;
};
/* A return draft: the reverse desk and the API save the type as "RVP" (OMS ShipmentType); older data and the
   All Shipments clone path say "REVERSE". Both are the same thing to a merchant. */
const isReverseType = (type) => ["REVERSE", "RVP"].includes(String(type || "").trim().toUpperCase());
/* A draft's title as the merchant should read it. Drafts re-ordered from a shipment before OMS #248 were titled with the
   internal shipment id ("Re-order from 4c3e…"); that id means nothing to a merchant and never appears in this UI. */
const draftTitleOf = (d) => {
  const title = d?.draft_title ? String(d.draft_title).trim() : "";
  if (!title) return null;
  return /^Re-order from [0-9a-f]{16,32}$/i.test(title) ? "Re-order" : title;
};

/* ──────────────────────────── Design vocabulary ────────────────────────────
   The All Shipments row dialect, cell for cell: 12px mono identity with an eyebrow, 13px names,
   11px facts, 9px eyebrows, ring chips, hover-revealed copy, h-7 square row controls. */
const TH = "px-2.5 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap shadow-[inset_0_-1px_0_#e2e8f0]";
const TD = "px-2.5 py-2.5 align-top";
const EYEBROW = "w-7 flex-shrink-0 text-[9px] font-bold uppercase tracking-wider";
const MICRO = "text-[10px] font-semibold uppercase tracking-wider text-slate-400";
const TYPE_CHIP = "inline-flex max-w-fit items-center rounded px-1 py-px text-[10px] font-semibold uppercase tracking-wide ring-1";
const COPY_BTN = "inline-flex align-middle ml-1 -mt-px p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100";
/* Labelled row action = the Error tab's Retry recipe; square controls = the row menu / expand recipe. */
const ROW_BTN = "px-2 py-1 text-xs font-medium rounded-md border transition-colors flex items-center justify-center gap-1 whitespace-nowrap";
const ROW_BTN_ACCENT = `${ROW_BTN} text-[#2A59BD] bg-[#3877FC]/[0.06] border-[#3877FC]/25 hover:bg-[#3877FC]/[0.12]`;
const ROW_BTN_AMBER = `${ROW_BTN} text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100`;
const ROW_BTN_OFF = `${ROW_BTN} text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed`;
const SQUARE_BTN = "flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-400 hover:text-[#2A59BD] transition-colors disabled:cursor-not-allowed disabled:opacity-50";
/* Toolbar buttons, exactly as on All Shipments: h-8, quiet when disabled, tinted when armed. */
const TOOL_BTN = "h-8 px-2.5 rounded-lg border text-xs font-semibold transition-colors flex items-center justify-center gap-1.5";
const TOOL_OFF = "border-slate-200 bg-white text-slate-300 cursor-not-allowed";
const DATE_PRESETS = [
  { label: "Today", days: 1 },
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
];
/* Modal buttons. */
const BTN = "inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 disabled:cursor-not-allowed disabled:opacity-50";
const BTN_NEUTRAL = `${BTN} border-slate-200 bg-white text-slate-600 hover:text-slate-900`;
const BTN_BLUE = `${BTN} border-[#3877FC] bg-[#3877FC] text-white hover:border-[#2A59BD] hover:bg-[#2A59BD]`;

/* A draft's state reads through one map: chip, edge, verb for the time line. */
const STATUS_TONE = {
  DRAFT: { chip: "bg-[#3877FC]/[0.08] text-[#2A59BD] ring-[#3877FC]/20", edge: "bg-[#3877FC]", verb: "Saved" },
  QUEUED: { chip: "bg-amber-50 text-amber-700 ring-amber-200", edge: "bg-amber-400", verb: "Queued" },
  PLACED: { chip: "bg-emerald-50 text-emerald-700 ring-emerald-200", edge: "bg-emerald-500", verb: "Booked" },
  FAILED: { chip: "bg-rose-50 text-rose-700 ring-rose-200", edge: "bg-rose-500", verb: "Failed" },
  DISCARDED: { chip: "bg-slate-100 text-slate-600 ring-slate-200", edge: "bg-slate-300", verb: "Discarded" },
};
const toneOf = (status) => STATUS_TONE[status] || STATUS_TONE.DISCARDED;

/* Tab badge (All Shipments recipe): solid when the tab is active, a tint when it merely has a count, quiet at zero. */
const tabBadgeClass = (tab, count, isActive) => {
  if (!count) return "bg-slate-100 text-slate-400 ring-1 ring-slate-200";
  if (isActive) return `${tab.badgeSolid} text-white shadow-sm`;
  return tab.badgeTint;
};

/* ──────────────────────────── Formatting (All Shipments' helpers) ──────────────────────────── */
/** Compact "how long ago" for table timestamps - "just now" / "34m ago" / "5h ago" / "12d ago". */
const relTime = (input) => {
  if (!input) return null;
  const t = new Date(input).getTime();
  if (Number.isNaN(t)) return null;
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};
const formatShortIST = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const now = new Date();
  const sameYear = date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric" }) === now.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric" });
  return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", ...(sameYear ? {} : { year: "numeric" }), month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};
const titleCaseName = (name) => (name ? String(name).toLowerCase().replace(/(^|[\s/-])\w/g, (c) => c.toUpperCase()) : name);
const carrierLabel = (raw) => {
  if (!raw) return null;
  const { carrier, mode } = formatCarrier(String(raw).replace(/_/g, " "));
  return mode ? `${carrier} ${mode}` : carrier;
};
/* Whole rupees stay whole ("₹5,781"); a fractional amount shows both paise digits ("₹5,781.50"), never "₹5,781.5". */
const fmtInr = (n, decimals = 0) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  const fractional = decimals > 0 && Math.abs(num - Math.round(num)) >= 0.005;
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: fractional ? Math.min(2, decimals) : 0, maximumFractionDigits: decimals })}`;
};
/* Money that is being compared (a balance against a cost) always shows both paise digits. */
const fmtMoney = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtWeight = (w) => {
  const v = Number(w?.value);
  if (!(v > 0)) return null;
  const unit = String(w.unit || "gm").toLowerCase();
  if (unit === "kg") return `${v} kg`;
  return v >= 1000 ? `${(v / 1000).toFixed(2).replace(/\.?0+$/, "")} kg` : `${v} g`;
};
const dimsLabel = (d) => (d && Number(d.length) > 0 && Number(d.breadth) > 0 && Number(d.height) > 0 ? `${d.length} × ${d.breadth} × ${d.height} ${String(d.unit || "cm").toLowerCase()}` : null);
const cityPin = (city, pin) => [city ? titleCaseName(city) : null, pin].filter(Boolean).join(", ");

/* Everything a row or card needs from one draft, derived once. */
const deriveRow = (d) => {
  const od = d.order_data || {};
  const pickup = od.pickup_details || {};
  const drop = od.drop_details || {};
  const orderDetails = od.shipment_details?.order_details || {};
  const packages = od.shipment_details?.shipment_packages || orderDetails.shipment_packages || [];
  const firstPkg = packages[0] || {};
  const allItems = packages.flatMap((pkg) => pkg.items || []);
  const itemCount = allItems.reduce((n, it) => n + (Number(it.quantity) || 1), 0);
  const itemsTotal = allItems.reduce((sum, it) => sum + ((Number(it.item_payment_details?.unit_price) || 0) * (Number(it.quantity) || 1)), 0);
  const invoiceValue = Number(orderDetails.order_invoice_details?.invoice_value);
  // OMS stores COD as cod_amount_details.value (create-order and the draft response both use { value, currency }).
  const codAmount = Number(orderDetails.cod_amount_details?.value ?? orderDetails.cod_amount_details?.cod_amount);
  const paymentType = String(orderDetails.order_type || od.payment_mode || "").toUpperCase();
  const amount = paymentType === "COD" && codAmount > 0 ? codAmount : (Number.isFinite(invoiceValue) && invoiceValue > 0 ? invoiceValue : itemsTotal);
  const status = d.draft_status;
  return {
    od, pickup, drop, orderDetails, packages, firstPkg, allItems, itemCount, itemsTotal, invoiceValue, codAmount, paymentType, amount,
    weight: fmtWeight(firstPkg.weight),
    dims: dimsLabel(firstPkg.dimensions),
    orderRef: orderDetails.order_reference_number || d.order_source_reference_id || "",
    isReverse: isReverseType(od.shipment_order_type),
    carrierName: od.shipment_details?.shipment_carrier_details?.carrier_partner_public_name || null,
    tone: toneOf(status),
    stampIso: status === "PLACED" ? (d.placed_at || d.updated_date_time)
      : status === "FAILED" ? (d.placement_error?.failed_at || d.updated_date_time)
        : (d.updated_date_time || d.created_date_time),
    hasError: status === "FAILED" && Boolean(d.placement_error),
  };
};

/* The booked ticket (the desks' BookedSuccess) fed from a draft plus the `placed_order` the revamped OMS returns. */
const ticketPropsFor = ({ draft, placedOrder: po, bookedAt }) => {
  const r = deriveRow(draft);
  const name = po.carrier_partner_public_name || r.carrierName || null;
  const { carrier, mode } = formatCarrier(name || "");
  const tat = po.max_tat_in_days != null ? Number(po.max_tat_in_days) : null;
  const charge = po.total_amount_charged_after_tax != null ? Number(po.total_amount_charged_after_tax) : null;
  const w = r.firstPkg.weight || {};
  const dims = r.firstPkg.dimensions || {};
  const inch = String(dims.unit || "cm").toLowerCase().startsWith("in");
  const cm = (v) => (inch ? Number(v) * 2.54 : Number(v));
  const deadKg = Number(w.value) > 0 ? (String(w.unit || "g").toLowerCase().startsWith("k") ? Number(w.value) : Number(w.value) / 1000) : null;
  const volKg = Number(dims.length) > 0 && Number(dims.breadth) > 0 && Number(dims.height) > 0 ? (cm(dims.length) * cm(dims.breadth) * cm(dims.height)) / 5000 : null;
  const billedKg = deadKg != null && volKg != null ? Math.max(deadKg, volKg) : null;
  const billedBasis = billedKg == null ? null : deadKg >= volKg ? "dead" : "vol";
  const isCod = r.paymentType === "COD";
  return {
    bookedAt,
    variant: r.isReverse ? "return" : "forward",
    origin: { city: r.pickup.pickup_city, pin: r.pickup.pickup_postal_code },
    destination: { city: r.drop.drop_city, pin: r.drop.drop_postal_code },
    courier: { name, carrier, mode, accent: carrierAccent(carrier), charge, tat, edd: tat != null ? estDeliveryDate(tat) : null },
    charge,
    settlement: null,
    billedKg,
    billedBasis,
    order: {
      recipientName: r.drop.drop_poc,
      recipientPhone: r.drop.drop_poc_contact_number,
      paymentLabel: isCod ? "COD" : "Prepaid",
      paymentValue: isCod && r.codAmount > 0 ? fmtInr(r.codAmount, 2) : (r.invoiceValue > 0 ? fmtInr(r.invoiceValue, 2) : null),
      itemsCount: r.allItems.length,
      orderRef: po.order_reference_number || r.orderRef,
      waybill: po.waybill,
    },
    labelUrl: po.label_url || null,
    invoiceUrl: po.invoice_url || null,
  };
};

/* ──────────────────────────── Copy affordance (All Shipments' CellCopy) ──────────────────────────── */
/** Copy affordance with the copied beat — check-swap for 1.4s. */
const CellCopy = ({ value, title, className = COPY_BTN }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-row-noclick=""
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText?.(String(value));
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
      }}
      className={className}
      title={title}
      aria-label={title}
    >
      {copied ? (
        <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );
};

/* ──────────────────────────── Status chip (All Shipments' StatusBadge chip) ──────────────────────────── */
const DraftStatusBadge = ({ status }) => (
  <span className={`inline-flex w-fit min-w-0 items-center px-2 py-0.5 rounded-full text-xs font-semibold leading-snug ring-1 ${toneOf(status).chip}`} title={getDraftOrderStatusName(status)}>
    {getDraftOrderStatusName(status)}
  </span>
);

/* Source reads like the sales-channel chip on All Shipments. */
const SourceBadge = ({ source }) => {
  const label = Object.values(DraftOrderSourceEnum).find((e) => e.code === source)?.name || source;
  if (!label) return null;
  return <span className={`${TYPE_CHIP} bg-indigo-50 ring-indigo-200 text-indigo-700 truncate`} title={label}>{label}</span>;
};

/* ──────────────────────────── Dialog focus: land inside, keep Tab inside, hand focus back ──────────────────────────── */
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const useDialogFocus = (open, ref) => {
  useEffect(() => {
    if (!open || !ref.current) return undefined;
    const node = ref.current;
    const previous = document.activeElement;
    node.focus();
    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const items = Array.from(node.querySelectorAll(FOCUSABLE));
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === node)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    node.addEventListener("keydown", onKey);
    return () => {
      node.removeEventListener("keydown", onKey);
      if (previous && typeof previous.focus === "function" && document.contains(previous)) previous.focus();
    };
  }, [open, ref]);
};

/* ──────────────────────────── Select menu (same control as All Shipments' filters) ──────────────────────────── */
const SelectMenu = ({ value, onChange, options, disabled = false, buttonClassName = "h-9", dropUp = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setIsOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setIsOpen(false); };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDocDown); document.removeEventListener("keydown", onKey); };
  }, [isOpen]);

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((o) => !o)}
        className={`w-full ${buttonClassName} px-3 text-sm border rounded-lg bg-white flex items-center justify-between gap-1.5 transition-colors ${disabled
          ? "bg-slate-50 text-slate-400 cursor-not-allowed border-slate-200"
          : isOpen
            ? "ring-2 ring-[#3877FC]/25 border-[#3877FC] text-slate-800"
            : "border-slate-200 text-slate-800"}`}
      >
        <span className={`truncate ${selected?.value === "" ? "text-slate-400" : ""}`}>{selected?.label}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>
      {isOpen && (
        <div className={`absolute z-[9999] w-full min-w-[10rem] bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden animate-scale-in ${dropUp ? "bottom-full mb-2" : "mt-2"}`}>
          <div className="max-h-64 overflow-y-auto py-1">
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { onChange(opt.value); setIsOpen(false); }}
                  className={`w-full px-3 min-h-9 py-2 flex items-center gap-2.5 text-sm transition-colors ${isSelected ? "bg-[#3877FC]/[0.06] text-[#2A59BD] font-semibold" : "hover:bg-[#3877FC]/[0.05] text-slate-700"}`}
                >
                  <span className="flex-1 text-left leading-snug">{opt.label}</span>
                  {isSelected && <Check className="w-4 h-4 text-[#3877FC] flex-shrink-0" strokeWidth={2.5} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ──────────────────────────── Confirm modal ──────────────────────────── */
const ConfirmModal = ({ open, title, message, extra = null, onConfirm, onCancel, confirmLabel = "Confirm", confirmDisabled = false, variant = "danger" }) => {
  const boxRef = useRef(null);
  useDialogFocus(open, boxRef);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);
  if (!open) return null;
  const danger = variant === "danger";
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <section ref={boxRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="draft-confirm-title" className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/80 outline-none">
        <div className={`h-1 ${danger ? "bg-rose-500" : "bg-[#3877FC]"}`} />
        <div className="px-6 pb-5 pt-5">
          <div className="flex items-start gap-3">
            <span className={`grid h-9 w-9 flex-shrink-0 place-items-center rounded-full ${danger ? "bg-rose-50 text-rose-600" : "bg-[#3877FC]/[0.08] text-[#2A59BD]"}`}>
              {danger ? <AlertTriangle className="h-4 w-4" /> : <Send className="h-4 w-4" />}
            </span>
            <div className="min-w-0">
              <h3 id="draft-confirm-title" className="text-[15px] font-semibold text-slate-900">{title}</h3>
              <div className="mt-1.5 text-[13px] leading-relaxed text-slate-600">{message}</div>
              {extra}
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={onCancel} className={BTN_NEUTRAL}>Cancel</button>
            <button type="button" disabled={confirmDisabled} onClick={() => { onConfirm(); onCancel(); }} className={`${BTN} text-white disabled:cursor-not-allowed disabled:opacity-50 ${danger ? "border-rose-600 bg-rose-600 hover:border-rose-700 hover:bg-rose-700" : "border-[#3877FC] bg-[#3877FC] hover:border-[#2A59BD] hover:bg-[#2A59BD]"}`}>{confirmLabel}</button>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
};

/* ──────────────────────────── Placement pre-flight (revamped OMS) ────────────────────────────
   The Place confirms ask the server what would stop each draft - wallet, a reference that already has a shipment,
   serviceability - before the merchant commits, and place only what passes. On an OMS without the endpoint the
   confirms keep today's client-side readiness check and nothing else changes. */
const PREFLIGHT_BLOCKER_COPY = {
  NOT_FOUND: "draft no longer exists",
  INVALID_STATUS: "not in a state that can be booked",
  MISSING_FIELDS: "required fields missing",
  REFERENCE_EXISTS: "order reference already has a shipment",
  NOT_SERVICEABLE: "no courier serves this pickup–delivery pair",
};
const PREFLIGHT_WARNING_COPY = {
  REFERENCE_FAILED_BEFORE: "this reference failed to book before",
  INSUFFICIENT_BALANCE: "wallet balance may not cover it",
};
const preflightReasons = (r) => {
  const out = [];
  (r.blockers || []).forEach((code) => {
    if (code === "MISSING_FIELDS") {
      const n = r.readiness?.blockers?.length || 0;
      out.push(n ? `${n} required field${n > 1 ? "s" : ""} missing` : PREFLIGHT_BLOCKER_COPY[code]);
    } else if (code === "REFERENCE_EXISTS") {
      out.push(`${PREFLIGHT_BLOCKER_COPY[code]}${r.reference?.awb_number ? ` (AWB ${r.reference.awb_number})` : ""}`);
    } else if (code === "INVALID_STATUS") {
      out.push(`${PREFLIGHT_BLOCKER_COPY[code]}${r.draft_status ? ` (${getDraftOrderStatusName(r.draft_status)})` : ""}`);
    } else {
      out.push(PREFLIGHT_BLOCKER_COPY[code] || String(code).toLowerCase().replace(/_/g, " "));
    }
  });
  // "Could not be checked" warnings are the server's own limitation, not something the merchant can act on.
  (r.warnings || []).forEach((code) => { if (PREFLIGHT_WARNING_COPY[code]) out.push(PREFLIGHT_WARNING_COPY[code]); });
  return out;
};
const hasVisibleWarning = (r) => (r.warnings || []).some((code) => PREFLIGHT_WARNING_COPY[code]);

/* ──────────────────────────── Placement run (bulk place) ────────────────────────────
   A bulk place answers with a run id on the revamped OMS; the card follows it until every chunk is done, then
   offers the placed and failed drafts as filtered views and a one-click retry. The plain (old OMS) response still
   gets the card, without the follow-along. */
const PlacementRunCard = ({ run, onShow, onRetryFailed, retrying, onRefresh, onDismiss }) => {
  const t = run.totals;
  const done = t.placed + t.failed;
  const total = t.queued || 0;
  const running = run.status === "RUNNING";
  const pct = run.chunksTotal ? Math.round((run.chunksDone / run.chunksTotal) * 100) : total ? Math.round((done / total) * 100) : 0;
  const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
  // Drafts still being processed when the run reports: queued minus the ones that reached a terminal state. They are
  // in flight on the server (async / confirmation), not lost — named so the numbers add up (placed + failed + these).
  const stillProcessing = Math.max(0, (total || 0) - (t.placed || 0) - (t.failed || 0));
  const title = running ? `Booking ${plural(total, "draft")} · ${done} of ${total} done`
    : run.status === "COMPLETED" ? `${t.placed} booked${t.failed ? ` · ${t.failed} failed` : ""}${stillProcessing ? ` · ${stillProcessing} still processing` : ""}${t.rejected ? ` · ${t.rejected} rejected` : ""}`
      : run.status === "ACCEPTED" ? `${plural(total, "draft")} queued for booking`
        : "Booking is taking longer than expected";
  const sub = running ? "Drafts move to Booked or Failed as the couriers answer. Keep working - this list refreshes as they land."
    : run.status === "COMPLETED" && run.interrupted ? "This batch was interrupted on the server before it finished. Drafts it never reached are back in Draft and can be booked again."
      : run.status === "COMPLETED" && stillProcessing ? "Some are still being confirmed by the courier. Refresh in a moment to see where they land."
      : run.status === "COMPLETED" ? (t.failed ? "Failed drafts keep their details and the reason, and can be retried." : t.placed ? "Every draft in this batch is now a live shipment." : "Nothing was booked in this batch.")
      : run.status === "ACCEPTED" ? "The server is booking them in the background. Refresh in a moment to see where they landed."
        : "The batch is still being worked through on the server. Refresh to check on it.";
  const btn = "inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50";
  return (
    <div className="border-b border-slate-200/80 bg-[#F8FAFF] px-3 py-2.5" role="status" aria-live="polite">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="mt-0.5 flex-shrink-0">
            {running ? <span className="block h-4 w-4 animate-spin rounded-full border-2 border-[#3877FC]/25 border-t-[#3877FC]" />
              : run.status === "COMPLETED" ? <CheckCircle2 className={`h-4 w-4 ${t.failed || run.interrupted ? "text-amber-500" : "text-emerald-600"}`} />
                : <AlertTriangle className="h-4 w-4 text-amber-500" />}
          </span>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-slate-900">{title}</div>
            <div className="text-[11.5px] text-slate-500">{sub}</div>
            {running && (
              <div className="mt-1.5 h-1 w-full max-w-[320px] overflow-hidden rounded-full bg-slate-200/80" aria-hidden="true">
                <div className="h-full rounded-full bg-[#3877FC] transition-[width] duration-500" style={{ width: `${Math.max(4, Math.min(100, pct))}%` }} />
              </div>
            )}
            {run.rejected?.length > 0 && (
              <details className="mt-1.5">
                <summary className="cursor-pointer select-none text-[11px] font-medium text-amber-700 hover:underline underline-offset-2">{plural(run.rejected.length, "draft")} could not be booked - why</summary>
                <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto">
                  {run.rejected.map((x, i) => (
                    <li key={`${x.draft_order_id}-${i}`} className="text-[11px] text-slate-600"><span className="font-mono text-slate-800 break-all">{x.draft_order_id}</span> — {x.reason || "rejected"}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1 md:flex-shrink-0">
          {run.runId && t.placed > 0 && <button type="button" onClick={() => onShow("PLACED")} className={`${btn} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}><PackageCheck className="h-3.5 w-3.5" /> Show booked</button>}
          {run.runId && t.failed > 0 && <button type="button" onClick={() => onShow("FAILED")} className={`${btn} border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100`}><XCircle className="h-3.5 w-3.5" /> Show failed</button>}
          {run.runId && run.status === "COMPLETED" && t.failed > 0 && <button type="button" onClick={onRetryFailed} disabled={retrying} className={`${btn} border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100`}><RotateCcw className={`h-3.5 w-3.5 ${retrying ? "animate-spin" : ""}`} /> Retry failed</button>}
          {(run.status === "ACCEPTED" || run.status === "STALE") && <button type="button" onClick={onRefresh} className={`${btn} border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>}
          {!running && <button type="button" onClick={onDismiss} className={SQUARE_BTN} title="Dismiss" aria-label="Dismiss booking summary"><X className="h-4 w-4" /></button>}
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────── Empty state ──────────────────────────── */
const EMPTY_COPY = {
  DRAFT: ["No drafts waiting", "Save an order as a draft from the create-order desks, or upload a CSV, and it lands here ready to book."],
  QUEUED: ["Nothing queued", "Drafts you book in bulk wait here for a moment while the couriers answer."],
  PLACED: ["No booked drafts yet", "Drafts that became live shipments show here with their AWB."],
  FAILED: ["No failed bookings", "When a courier refuses a booking you will see why here, with a retry."],
  all: ["No drafts match", "Try widening the date range or clearing a filter."],
};
const EmptyState = ({ tab, onReset, hasFilters }) => {
  const [title, body] = EMPTY_COPY[tab] || EMPTY_COPY.all;
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-400"><FileText className="h-6 w-6" /></span>
      <h3 className="mt-4 text-[15px] font-semibold text-slate-900">{title}</h3>
      <p className="mt-1 max-w-md text-[13px] leading-relaxed text-slate-500">{body}</p>
      {hasFilters && <button type="button" onClick={onReset} className={`${BTN_NEUTRAL} mt-5`}>Reset filters</button>}
    </div>
  );
};

/* ──────────────────────────── Table skeleton ──────────────────────────── */
const TableSkeleton = () => (
  <div className="divide-y divide-slate-100" role="status" aria-label="Loading drafts">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="flex items-center gap-4 px-4 py-4">
        <div className="cfo-skeleton h-4 w-4 rounded" />
        <div className="cfo-skeleton h-3.5 w-28 rounded" />
        <div className="cfo-skeleton h-3.5 w-44 rounded" />
        <div className="cfo-skeleton h-3.5 w-20 rounded" />
        <div className="cfo-skeleton h-3.5 w-24 rounded" />
        <div className="cfo-skeleton h-5 w-16 rounded-full" />
        <div className="ml-auto cfo-skeleton h-7 w-7 rounded-md" />
      </div>
    ))}
  </div>
);

/* ──────────────────────── Row menu (All Shipments' ActionMenu: h-7 square trigger, calm rows, colour only on place / destroy) ──────────────────────── */
const ActionMenu = ({ draft, onAction, onOpenChange, validationErrors = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const allowedKeys = ACTIONS_BY_STATUS[draft.draft_status] || ["view"];
  const isRetryable = draft.draft_status === "FAILED" && draft.placement_error?.retryable;
  const missing = validationErrors.length;

  const ALL_ACTION_DEFS = [
    { key: "view", label: "View details", Icon: Eye },
    { key: "place", label: "Book shipment", Icon: Send, disabled: missing > 0, reason: missing > 0 ? `${missing} field${missing > 1 ? "s" : ""} missing` : null, tone: "accent" },
    { key: "retry", label: "Retry booking", Icon: RotateCcw, disabled: !isRetryable, reason: !isRetryable ? "Edit the draft first" : null, tone: "accent" },
    { key: "edit", label: "Edit draft", Icon: Pencil },
    { key: "duplicate", label: "Duplicate", Icon: Copy },
    { key: "viewShipment", label: "View shipment", Icon: ExternalLink },
    { key: "permanentDelete", label: "Delete", Icon: Trash2, tone: "danger", divider: true },
  ];
  const actions = ALL_ACTION_DEFS.filter((a) => allowedKeys.includes(a.key));
  const rowTone = (a) => {
    if (a.disabled) return "text-slate-400 cursor-not-allowed";
    if (a.tone === "danger") return "text-rose-600 hover:bg-rose-50 [&_svg]:text-rose-500";
    if (a.tone === "accent") return "text-[#2A59BD] hover:bg-[#3877FC]/[0.06] [&_svg]:text-[#3877FC]";
    return "text-slate-700 hover:bg-slate-50 [&_svg]:text-slate-400";
  };

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 240;
      const menuHeight = actions.length * 36 + 12;
      let top = rect.bottom + 6;
      let left = rect.right - menuWidth;
      if (top + menuHeight > window.innerHeight) top = rect.top - menuHeight - 6;
      if (left < 8) left = 8;
      if (left + menuWidth > window.innerWidth) left = window.innerWidth - menuWidth - 8;
      setPosition({ top, left });
    }
    const next = !isOpen;
    setIsOpen(next);
    onOpenChange?.(next);
  };
  const closeMenu = useCallback(() => { setIsOpen(false); onOpenChange?.(false); }, [onOpenChange]);
  useEffect(() => {
    if (!isOpen) return undefined;
    const h = (e) => { if (buttonRef.current && !buttonRef.current.contains(e.target) && menuRef.current && !menuRef.current.contains(e.target)) closeMenu(); };
    const k = (e) => { if (e.key === "Escape") closeMenu(); };
    document.addEventListener("mousedown", h);
    document.addEventListener("keydown", k);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", k); };
  }, [isOpen, closeMenu]);

  const menuNode = isOpen && ReactDOM.createPortal(
    <div ref={menuRef} role="menu" className="fixed z-[9999] w-60 bg-white rounded-xl shadow-xl ring-1 ring-slate-200 py-1.5 animate-scale-in" style={{ top: position.top, left: position.left }}>
      {actions.map((a) => (
        <React.Fragment key={a.key}>
          {a.divider && <div className="my-1 h-px bg-slate-100" aria-hidden="true" />}
          <button
            type="button"
            role="menuitem"
            onClick={() => { if (!a.disabled) { onAction(a.key, draft); closeMenu(); } }}
            disabled={a.disabled}
            className={`w-full h-9 px-3 flex items-center gap-2.5 text-[13px] font-medium transition-colors ${rowTone(a)}`}
          >
            <a.Icon className="w-4 h-4 flex-shrink-0" />
            <span>{a.label}</span>
            {a.disabled && a.reason && <span className="ml-auto text-[10px] font-normal text-slate-300 whitespace-nowrap">{a.reason}</span>}
          </button>
        </React.Fragment>
      ))}
    </div>,
    document.body,
  );

  return (
    <div className="relative" data-row-noclick="">
      <button ref={buttonRef} type="button" onClick={handleToggle} title="Actions" aria-label="Row actions" aria-haspopup="menu" aria-expanded={isOpen} className={`h-7 w-7 rounded-md border transition-colors flex items-center justify-center ${isOpen ? "border-slate-300 bg-slate-100 text-slate-900" : "border-slate-200 bg-white text-slate-400 hover:text-slate-900"}`}>
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {menuNode}
    </div>
  );
};

/* ──────────────────────── Bulk upload modal ──────────────────────── */
const BulkUploadModal = ({ open, onClose, onUpload }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const boxRef = useRef(null);
  useDialogFocus(open, boxRef);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !uploading) onClose(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, uploading, onClose]);
  if (!open) return null;

  const pick = (f) => { if (f && /\.csv$/i.test(f.name)) setFile(f); else if (f) toast.error("Only .csv files are accepted"); };
  const doUpload = async () => {
    if (!file) return;
    setUploading(true);
    try { await onUpload(file); setFile(null); onClose(true); } catch { /* parent handles */ } finally { setUploading(false); }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]" onMouseDown={(e) => { if (e.target === e.currentTarget && !uploading) onClose(false); }}>
      <section ref={boxRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="bulk-upload-title" className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/80 outline-none">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
          <div>
            <h3 id="bulk-upload-title" className="text-[15px] font-semibold text-slate-900">Upload drafts from a CSV</h3>
            <p className="mt-0.5 text-[12.5px] text-slate-500">Every row becomes a draft you can review and book from this page.</p>
          </div>
          <button type="button" onClick={() => onClose(false)} disabled={uploading} aria-label="Close" className={SQUARE_BTN}><X className="h-4 w-4" /></button>
        </div>
        <div className="px-6 py-5">
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileRef.current?.click()}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileRef.current?.click(); } }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
            className={`cursor-pointer rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${file ? "border-[#3877FC]/50 bg-[#3877FC]/[0.04]" : dragging ? "border-[#3877FC] bg-[#3877FC]/[0.06]" : "border-slate-300 hover:border-[#3877FC]/60 hover:bg-[#3877FC]/[0.03]"}`}
          >
            <input ref={fileRef} type="file" accept=".csv" onChange={(e) => pick(e.target.files?.[0] || null)} className="hidden" />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#3877FC]/[0.10] text-[#2A59BD]"><FileSpreadsheet className="h-5 w-5" /></span>
                <div className="text-left">
                  <p className="text-[13px] font-semibold text-slate-900">{file.name}</p>
                  <p className="text-[12px] text-slate-500">{(file.size / 1024).toFixed(1)} KB · click to change</p>
                </div>
              </div>
            ) : (
              <>
                <span className="mx-auto grid h-10 w-10 place-items-center rounded-xl bg-slate-100 text-slate-500"><Upload className="h-5 w-5" /></span>
                <p className="mt-3 text-[13px] font-semibold text-slate-800">Drop a CSV here, or click to choose</p>
                <p className="mt-0.5 text-[12px] text-slate-500">Only .csv files are accepted · up to 5,000 rows</p>
              </>
            )}
          </div>
          {/* The same file format as Create Bulk Order, so one template serves both. */}
          <div className="mt-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2 rounded-xl bg-slate-50 px-3.5 py-3 ring-1 ring-slate-200/70">
            <div className="min-w-0 text-[12px] leading-relaxed text-slate-600">
              <p className="font-semibold text-slate-800">Need the format?</p>
              <p>Same columns as Create Bulk Order: order reference, payment mode, pickup and delivery addresses with pincodes, package size and weight, items, invoice value.</p>
            </div>
            <a href="/sample-bulk-order.csv" download="sample-bulk-order.csv" onClick={(e) => e.stopPropagation()} className={`${BTN_NEUTRAL} flex-shrink-0 no-underline`}>
              <Download className="h-3.5 w-3.5" /> Download sample CSV
            </a>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-3">
          <button type="button" onClick={() => onClose(false)} disabled={uploading} className={BTN_NEUTRAL}>Cancel</button>
          <button type="button" onClick={doUpload} disabled={!file || uploading} className={BTN_BLUE}>
            {uploading ? <><span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" /> Uploading…</> : <><Upload className="h-3.5 w-3.5" /> Upload and create drafts</>}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
};

/* ──────────────────────── Details panel: the All Shipments expanded row, for a draft ──────────────────────── */
const Fact = ({ label, value, mono = false }) => (value ? (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-xs text-slate-500 flex-shrink-0">{label}</span>
    <span className={`text-xs font-medium text-slate-900 text-right break-all ${mono ? "font-mono" : ""}`}>{value}</span>
  </div>
) : null);

const DraftDetailsPanel = ({ draft: d, row, missing = [], retrying = false, onEdit, onRetry, onOpenShipment, onCollapse }) => {
  const { pickup, drop, orderDetails, allItems, itemsTotal, invoiceValue, codAmount, paymentType, weight, dims, carrierName, isReverse, hasError } = row;
  // A booked draft's shipment is opened by the order reference it was booked under (never by the internal shipment id).
  const shipmentRef = d.draft_status === "PLACED" ? (d.placed_order_summary?.order_reference_number || row.orderRef || null) : null;
  const totalQty = allItems.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
  const err = d.placement_error;
  return (
    <div className="px-3 py-3 md:px-4 animate-slide-down">
      <div className="rounded-xl bg-slate-50/50 ring-1 ring-slate-200/70 px-4 py-4 md:px-6 md:py-5 space-y-5">
        {hasError && (
          <div className="rounded-lg bg-white ring-1 ring-rose-200 p-3">
            <div className="flex items-start gap-2.5">
              <span className="mt-px grid h-5 w-5 flex-shrink-0 place-items-center rounded-full bg-rose-500 text-white"><X className="h-3 w-3" strokeWidth={3} /></span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-rose-700">Couldn't book this draft</p>
                <p className="mt-0.5 text-xs leading-relaxed text-slate-800">{placementErrorMessage(err.error_message)}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
                  {err.error_code && <span className="font-mono text-slate-400" title="Reference code, in case support asks for it">{err.error_code}</span>}
                  {(err.failed_at || d.updated_date_time) && <span>Failed {formatShortIST(err.failed_at || d.updated_date_time)}</span>}
                  {err.failed_carrier_partner_id && <span>Courier {carrierLabel(err.failed_carrier_partner_id)}</span>}
                  <span className={`${TYPE_CHIP} normal-case tracking-normal ${err.retryable ? "bg-emerald-50 ring-emerald-200 text-emerald-700" : "bg-slate-100 ring-slate-200 text-slate-600"}`}>{err.retryable ? "Can be retried" : "Edit before retrying"}</span>
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={onRetry} disabled={retrying || !err.retryable} className={err.retryable && !retrying ? ROW_BTN_AMBER : ROW_BTN_OFF}>
                    <RotateCcw className={`h-3.5 w-3.5 ${retrying ? "animate-spin" : ""}`} /> {retrying ? "Retrying…" : "Retry booking"}
                  </button>
                  <button type="button" onClick={onEdit} className={`${ROW_BTN} text-slate-700 bg-white border-slate-200 hover:bg-slate-50`}><Pencil className="h-3.5 w-3.5" /> Edit draft and retry</button>
                </div>
              </div>
            </div>
            {d.placement_attempts?.length > 0 && (
              <div className="mt-3 overflow-hidden rounded-lg ring-1 ring-slate-200/70">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      {["#", "Courier", "Outcome", "Code", "Message", "When"].map((h) => <th key={h} className="px-2.5 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400 whitespace-nowrap">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {d.placement_attempts.map((attempt, aidx) => (
                      <tr key={aidx}>
                        <td className="px-2.5 py-1.5 tabular-nums text-slate-500">{aidx + 1}</td>
                        <td className="px-2.5 py-1.5 font-medium text-slate-800 whitespace-nowrap">{carrierLabel(attempt.carrier_partner_id || attempt.error_provider) || "—"}</td>
                        <td className="px-2.5 py-1.5"><span className={`${TYPE_CHIP} ${attempt.success ? "bg-emerald-50 ring-emerald-200 text-emerald-700" : "bg-rose-50 ring-rose-200 text-rose-700"}`}>{attempt.success ? "Success" : "Failed"}</span></td>
                        <td className="px-2.5 py-1.5 font-mono text-slate-600">{attempt.error_code || "—"}</td>
                        <td className="max-w-[260px] truncate px-2.5 py-1.5 text-slate-600" title={placementErrorMessage(attempt.error_message)}>{placementErrorMessage(attempt.error_message) || "—"}</td>
                        <td className="px-2.5 py-1.5 whitespace-nowrap text-slate-500">{attempt.timestamp ? formatShortIST(attempt.timestamp) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {missing.length > 0 && (
          <div className="rounded-lg bg-amber-50/70 ring-1 ring-amber-200 px-3 py-2.5 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-bold text-amber-800">Needed before booking</p>
              <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                {missing.map((m, i) => <li key={i} className="text-xs text-slate-700"><span className="text-slate-400">{m.section} · </span>{m.field}</li>)}
              </ul>
            </div>
            <button type="button" onClick={onEdit} className={`${ROW_BTN_ACCENT} flex-shrink-0`}><Pencil className="h-3.5 w-3.5" /> Edit the draft</button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-5">
          <div className="flex flex-col gap-1 min-w-0">
            <span className={MICRO}>{isReverse ? "Pickup (customer)" : "Pickup"}</span>
            <span className="text-sm font-medium text-slate-900">{titleCaseName(pickup.pickup_organisation || pickup.pickup_poc) || "—"}</span>
            {pickup.pickup_poc && pickup.pickup_organisation && pickup.pickup_poc !== pickup.pickup_organisation && <span className="text-xs text-slate-500">{titleCaseName(pickup.pickup_poc)}</span>}
            {pickup.pickup_address && <span className="text-xs text-slate-600 leading-relaxed">{pickup.pickup_address}{pickup.pickup_landmark ? `, ${pickup.pickup_landmark}` : ""}</span>}
            <span className="text-xs text-slate-600">
              {[pickup.pickup_city ? titleCaseName(pickup.pickup_city) : null, pickup.pickup_state ? titleCaseName(pickup.pickup_state) : null].filter(Boolean).join(", ") || "—"}
              {pickup.pickup_postal_code ? <> {" - "}<span className="tabular-nums">{pickup.pickup_postal_code}</span><CellCopy value={pickup.pickup_postal_code} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors" /></> : null}
            </span>
            {pickup.pickup_poc_contact_number && <span className="text-xs text-slate-500 tabular-nums">{pickup.pickup_poc_contact_number}</span>}
          </div>
          <div className="flex flex-col gap-1 min-w-0">
            <span className={MICRO}>{isReverse ? "Delivery (warehouse)" : "Delivery"}</span>
            <span className="text-sm font-medium text-slate-900">{titleCaseName(drop.drop_poc) || <span className="text-slate-400 font-normal">Customer name missing</span>}</span>
            {drop.drop_address && <span className="text-xs text-slate-600 leading-relaxed">{drop.drop_address}{drop.drop_landmark ? `, ${drop.drop_landmark}` : ""}</span>}
            <span className="text-xs text-slate-600">
              {[drop.drop_city ? titleCaseName(drop.drop_city) : null, drop.drop_state ? titleCaseName(drop.drop_state) : null].filter(Boolean).join(", ") || "—"}
              {drop.drop_postal_code ? <> {" - "}<span className="tabular-nums">{drop.drop_postal_code}</span><CellCopy value={drop.drop_postal_code} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors" /></> : null}
            </span>
            {drop.drop_poc_contact_number && <span className="text-xs text-slate-500 tabular-nums">{drop.drop_poc_contact_number}<CellCopy value={drop.drop_poc_contact_number} title="Copy phone" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors" /></span>}
            {drop.drop_poc_email && <span className="text-xs text-slate-500 break-all">{drop.drop_poc_email}</span>}
          </div>
          <div className="flex flex-col gap-1.5 min-w-0">
            <span className={MICRO}>Package</span>
            <Fact label="Declared weight" value={weight} />
            <Fact label="Dimensions" value={dims} />
            <Fact label="Items" value={allItems.length ? `${allItems.length} line${allItems.length > 1 ? "s" : ""} · ${totalQty} unit${totalQty === 1 ? "" : "s"}` : null} />
            <Fact label="Courier" value={carrierName ? titleCaseName(carrierName) : "Smart allocation"} />
            {!weight && !dims && <span className="text-xs text-slate-400">No package details yet</span>}
          </div>
          <div className="flex flex-col gap-1.5 min-w-0">
            <span className={MICRO}>Payment &amp; invoice</span>
            <Fact label="Payment" value={paymentType ? (paymentType === "COD" ? "Cash on delivery" : "Prepaid") : null} />
            <Fact label={paymentType === "COD" ? "COD to collect" : "Invoice value"} value={fmtInr(paymentType === "COD" && codAmount > 0 ? codAmount : invoiceValue, 2)} />
            {paymentType === "COD" && invoiceValue > 0 && codAmount > 0 && codAmount !== invoiceValue && <Fact label="Invoice value" value={fmtInr(invoiceValue, 2)} />}
            <Fact label="Invoice no." value={orderDetails.order_invoice_details?.invoice_number} mono />
            <Fact label="Invoice date" value={orderDetails.order_invoice_details?.invoice_date ? formatShortIST(orderDetails.order_invoice_details.invoice_date) : null} />
            {itemsTotal > 0 && <Fact label="Items total" value={fmtInr(itemsTotal, 2)} />}
          </div>
        </div>

        {allItems.length > 0 && (
          <div>
            <span className={MICRO}>Items</span>
            <div className="mt-1.5 overflow-x-auto rounded-lg ring-1 ring-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {[["Item", "text-left"], ["SKU", "text-left"], ["HSN", "text-left"], ["Qty", "text-right"], ["Unit price", "text-right"], ["Amount", "text-right"]].map(([h, align]) => (
                      <th key={h} className={`px-3 py-2 ${align} text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {allItems.map((it, i) => {
                    const g = it.item_general_details || {};
                    const qty = Number(it.quantity) || 0;
                    const unit = Number(it.item_payment_details?.unit_price);
                    return (
                      <tr key={i}>
                        <td className="px-3 py-2 text-sm font-medium text-slate-900">{g.item_name || <span className="text-slate-400 font-normal">Unnamed item</span>}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">{g.sku || "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">{g.hsn_code || "—"}</td>
                        <td className="px-3 py-2 text-right text-sm tabular-nums text-slate-800">{qty || "—"}</td>
                        <td className="px-3 py-2 text-right text-sm tabular-nums text-slate-800">{Number.isFinite(unit) ? fmtInr(unit, 2) : "—"}</td>
                        <td className="px-3 py-2 text-right text-sm tabular-nums font-semibold text-slate-900">{Number.isFinite(unit) && qty ? fmtInr(unit * qty, 2) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {itemsTotal > 0 && (
                  <tfoot className="bg-slate-50/70 border-t border-slate-200">
                    <tr>
                      <td colSpan={5} className="px-3 py-2 text-right text-xs text-slate-500">Subtotal · {totalQty} unit{totalQty === 1 ? "" : "s"}</td>
                      <td className="px-3 py-2 text-right text-sm tabular-nums font-semibold text-slate-900">{fmtInr(itemsTotal, 2)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-5">
          <div className="flex flex-col gap-1.5 min-w-0">
            <span className={MICRO}>Draft</span>
            <Fact label="Draft id" value={d.draft_order_id} mono />
            <Fact label="Title" value={d.draft_title} />
            <Fact label="Tags" value={d.tags?.length ? d.tags.join(", ") : null} />
            {d.internal_notes && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-slate-500">Notes</span>
                <span className="text-xs text-slate-700 leading-relaxed">{d.internal_notes}</span>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1.5 min-w-0">
            <span className={MICRO}>Source</span>
            <Fact label="Source" value={Object.values(DraftOrderSourceEnum).find((e) => e.code === d.order_source)?.name || d.order_source} />
            <Fact label="Channel" value={d.order_source_channel_name} />
            <Fact label="External ref" value={d.order_source_reference_id} mono />
            {/* A draft duplicated from a draft names it; one re-ordered from a shipment says so without the shipment's internal id. */}
            <Fact label="Duplicated from" value={d.duplicated_from_draft_id} mono />
            {!d.duplicated_from_draft_id && d.duplicated_from_order_id && <Fact label="Created from" value="A booked shipment" />}
          </div>
          <div className="flex flex-col gap-1.5 min-w-0">
            <span className={MICRO}>History</span>
            <Fact label="Created" value={d.created_date_time ? `${formatShortIST(d.created_date_time)}${d.created_by ? ` · ${d.created_by}` : ""}` : null} />
            <Fact label="Updated" value={d.updated_date_time ? `${formatShortIST(d.updated_date_time)}${d.updated_by ? ` · ${d.updated_by}` : ""}` : null} />
            {/* What the placement booked (revamped OMS keeps the AWB and courier on the draft) */}
            <Fact label="AWB" value={d.placed_order_summary?.waybill} mono />
            <Fact label="Booked with" value={d.placed_order_summary?.carrier_partner_public_name ? titleCaseName(d.placed_order_summary.carrier_partner_public_name) : null} />
            {shipmentRef && (
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs text-slate-500 flex-shrink-0">Shipment</span>
                <span className="text-xs font-medium text-right break-all">
                  {/* The shipment is known by the order reference it was booked under */}
                  <button type="button" onClick={onOpenShipment} className="font-mono text-[#2A59BD] hover:underline underline-offset-2" title="Open the shipment">{shipmentRef}</button>
                </span>
              </div>
            )}
            {d.draft_status === "PLACED" && <Fact label="Booked" value={d.placed_at ? `${formatShortIST(d.placed_at)}${d.placed_by ? ` · ${d.placed_by}` : ""}` : null} />}
          </div>
        </div>

        {/* The All Shipments expanded-row footer: fold it back, or go to the full thing. For a draft the full thing is
            the order desk; for a booked one it is the shipment page. */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-200/70 pt-3">
          <button type="button" onClick={onCollapse} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-800">
            <ChevronUp className="h-3.5 w-3.5" /> Collapse
          </button>
          {shipmentRef ? (
            <button type="button" onClick={onOpenShipment} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#3877FC]/30 bg-white px-3 text-xs font-semibold text-[#2A59BD] transition-colors hover:bg-[#3877FC]/[0.06]">
              View shipment <ArrowRight className="h-3.5 w-3.5" />
            </button>
          ) : (d.draft_status === "DRAFT" || d.draft_status === "FAILED") ? (
            <button type="button" onClick={onEdit} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#3877FC]/30 bg-white px-3 text-xs font-semibold text-[#2A59BD] transition-colors hover:bg-[#3877FC]/[0.06]">
              Open in order desk <ArrowRight className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>

      </div>
    </div>
  );
};

/* ──────────────────────────── Courier identity + picker ────────────────────────────
   The courier reads like the ticket's Courier fact: a tinted initials tile, the carrier's name, its service mode as a
   chip. Smart allocation gets the same weight with a sparkle tile and one plain sentence. The picker is a proper menu:
   sorted by price, Cheapest and Fastest tagged (ties share the tag), a "by <date>" for each, the chosen row ticked. */
const CourierTile = ({ name, size = "h-8 w-8 text-[12px]" }) => {
  const { carrier } = formatCarrier(name || "");
  const accent = carrierAccent(carrier);
  return (
    <span className={`grid ${size} flex-shrink-0 place-items-center rounded-lg font-extrabold`}
      style={{ backgroundImage: `radial-gradient(circle at 30% 22%, ${accent}33, transparent 62%), linear-gradient(150deg, ${accent}1F, ${accent}0A)`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}2E` }}>
      {initialsOf(carrier || "?")}
    </span>
  );
};
const ModeChip = ({ mode }) => {
  if (!mode) return null;
  const Icon = MODE_ICON[mode];
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-wide ring-1 ring-inset ${MODE_STYLE[mode] || "bg-slate-100 text-slate-500 ring-slate-200/70"}`}>
      {Icon && <Icon className="h-2.5 w-2.5" strokeWidth={2.25} />}{mode}
    </span>
  );
};
const CourierIdentity = ({ name }) => {
  if (!name) {
    return (
      <span className="flex items-center gap-2 min-w-0">
        <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg bg-[#3877FC]/[0.08] text-[#2A59BD] ring-1 ring-inset ring-[#3877FC]/15"><Sparkles className="h-4 w-4" /></span>
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold leading-tight text-slate-900">Smart allocation</span>
          <span className="block text-[11px] leading-tight text-slate-500">Best courier picked when booking</span>
        </span>
      </span>
    );
  }
  const { carrier, mode } = formatCarrier(name);
  return (
    <span className="flex items-center gap-2 min-w-0">
      <CourierTile name={name} />
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-semibold leading-tight text-slate-900" title={carrier}>{carrier}</span>
        <ModeChip mode={mode} />
      </span>
    </span>
  );
};
/* Ranking: Cheapest = lowest price, Fastest = fewest days; every option that ties gets the tag. */
const rankCourierOptions = (options) => {
  const prices = options.map((o) => Number(o.price)).filter(Number.isFinite);
  const tats = options.map((o) => Number(o.max_tat_in_days)).filter(Number.isFinite);
  const minPrice = prices.length ? Math.min(...prices) : null;
  const minTat = tats.length ? Math.min(...tats) : null;
  return [...options]
    .sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0))
    .map((o) => ({
      ...o,
      cheapest: minPrice != null && Math.abs(Number(o.price) - minPrice) < 0.005,
      fastest: minTat != null && Number(o.max_tat_in_days) === minTat,
    }));
};
const CourierMenu = ({ options, chosen, onPick, position, menuRef }) => {
  const ranked = rankCourierOptions(options);
  const width = Math.max(position.width, 360);
  // The trigger sits in the last-but-one column, so a 360px menu anchored to its left edge would run off the
  // viewport: keep it fully on screen with a 12px gutter.
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1440;
  const left = Math.max(12, Math.min(position.left, viewportWidth - width - 12));
  return (
    <div ref={menuRef} className="fixed z-[99999] overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)] ring-1 ring-slate-200 animate-scale-in"
      style={{ left, width, ...(position.openUp ? { bottom: position.bottom } : { top: position.top }) }}>
      <div className="flex items-baseline justify-between border-b border-slate-100 px-3.5 py-2.5">
        <span className="text-[13px] font-semibold text-slate-900">Choose a courier</span>
        <span className="text-[11px] text-slate-500">{ranked.length} option{ranked.length === 1 ? "" : "s"} · incl. GST</span>
      </div>
      <div className="max-h-[340px] overflow-y-auto p-1.5">
        <button type="button" onClick={() => onPick(null)} className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors ${!chosen ? "bg-[#3877FC]/[0.07] ring-1 ring-inset ring-[#3877FC]/25" : "hover:bg-slate-50"}`}>
          <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-[#3877FC]/[0.08] text-[#2A59BD] ring-1 ring-inset ring-[#3877FC]/15"><Sparkles className="h-4 w-4" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-semibold leading-tight text-slate-900">Smart allocation</span>
            <span className="block text-[11px] leading-tight text-slate-500">We pick the best courier when booking</span>
          </span>
          {!chosen && <Check className="h-4 w-4 flex-shrink-0 text-[#2A59BD]" strokeWidth={2.5} />}
        </button>
        <div className="mx-2.5 my-1.5 border-t border-dashed border-slate-200" />
        {ranked.map((opt) => {
          const { carrier, mode } = formatCarrier(opt.name);
          const selected = chosen === opt.name;
          const eta = estDeliveryDate(opt.max_tat_in_days);
          return (
            <button key={opt.id} type="button" onClick={() => onPick(opt.name)} className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors ${selected ? "bg-[#3877FC]/[0.07] ring-1 ring-inset ring-[#3877FC]/25" : "hover:bg-slate-50"}`}>
              <CourierTile name={opt.name} size="h-9 w-9 text-[13px]" />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[13px] font-semibold leading-tight text-slate-900">{carrier}</span>
                  <ModeChip mode={mode} />
                </span>
                <span className="mt-0.5 flex flex-wrap items-center gap-1">
                  {opt.cheapest && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-wide text-emerald-700 ring-1 ring-inset ring-emerald-200"><IndianRupee className="h-2.5 w-2.5" strokeWidth={2.5} /> Cheapest</span>}
                  {opt.fastest && <span className="inline-flex items-center gap-1 rounded-full bg-[#3877FC]/[0.08] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-wide text-[#2A59BD] ring-1 ring-inset ring-[#3877FC]/20"><Zap className="h-2.5 w-2.5" strokeWidth={2.5} /> Fastest</span>}
                  {eta && <span className="text-[11px] text-slate-500">by {eta}</span>}
                </span>
              </span>
              <span className="flex flex-shrink-0 items-center gap-2">
                <span className="text-[14px] font-bold tabular-nums text-slate-900">{fmtMoney(opt.price)}</span>
                {selected && <Check className="h-4 w-4 text-[#2A59BD]" strokeWidth={2.5} />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ──────────────────────────── Booking confirm: the road ────────────────────────────
   Booking is the moment a draft turns into a real pickup, so the dialog shows it that way: a road from the pickup to
   the customer with the trucks about to leave, one per shipment (five at most, then a count). The pre-flight answer
   lands under it as ready / blocked / warnings tiles, the wallet line and the reasons per draft. */
const RoadWithTrucks = ({ count, fromLabel, fromSub, toLabel, toSub, checking }) => {
  const shown = Math.min(Math.max(count, 1), 5);
  const extra = count - shown;
  return (
    <div className="relative overflow-hidden rounded-t-2xl border-b border-[#E3E9F2] px-6 pb-5 pt-6 sm:px-8" style={{ background: "linear-gradient(to bottom, rgba(56,119,252,0.09), rgba(56,119,252,0.03))" }}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ backgroundImage: "radial-gradient(rgba(30,58,109,0.10) 1px, transparent 1.4px)", backgroundSize: "18px 18px", maskImage: "linear-gradient(to bottom, transparent, black 40%, black 75%, transparent)", WebkitMaskImage: "linear-gradient(to bottom, transparent, black 40%, black 75%, transparent)" }} />
      <div className="relative flex items-end justify-between gap-4">
        <div className="min-w-0 w-28 sm:w-36">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-white text-[#2A59BD] shadow-sm ring-1 ring-[#3877FC]/20"><Warehouse className="h-[18px] w-[18px]" /></span>
          <div className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">From</div>
          <div className="truncate text-[13px] font-semibold text-slate-900" title={fromLabel}>{fromLabel}</div>
          {fromSub && <div className="truncate text-[11px] text-slate-500">{fromSub}</div>}
        </div>
        <div className="relative flex-1 self-center pt-4">
          <div className="border-t-2 border-dotted border-slate-300/90" />
          <div className="absolute inset-x-0 -top-2 flex items-end justify-center gap-2">
            {Array.from({ length: shown }).map((_, i) => (
              <span key={i} className={`grid h-9 w-9 place-items-center rounded-xl bg-[#3877FC] text-white shadow-[0_8px_18px_-10px_rgba(56,119,252,0.9)] ring-2 ring-white ${checking ? "dro-truck-idle" : "dro-truck-ready"}`} style={{ animationDelay: `${i * 140}ms` }}>
                <Truck className="h-[18px] w-[18px]" strokeWidth={2.25} />
              </span>
            ))}
            {extra > 0 && <span className="grid h-9 min-w-[36px] place-items-center rounded-xl bg-white px-2 text-[12px] font-bold text-[#2A59BD] shadow-sm ring-1 ring-[#3877FC]/25">+{extra}</span>}
          </div>
        </div>
        <div className="min-w-0 w-28 text-right sm:w-36">
          <span className="ml-auto grid h-9 w-9 place-items-center rounded-xl bg-white text-emerald-600 shadow-sm ring-1 ring-emerald-200"><MapPin className="h-[18px] w-[18px]" /></span>
          <div className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">To</div>
          <div className="truncate text-[13px] font-semibold text-slate-900" title={toLabel}>{toLabel}</div>
          {toSub && <div className="truncate text-[11px] text-slate-500">{toSub}</div>}
        </div>
      </div>
    </div>
  );
};
const BookConfirmModal = ({ open, kind, drafts, skipped = 0, preflight, ids, labelFor, onConfirm, onCancel, confirmLabel, confirmDisabled, singleBlocked }) => {
  const boxRef = useRef(null);
  useDialogFocus(open, boxRef);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);
  if (!open) return null;

  const rows = drafts.map((d) => ({ d, r: deriveRow(d) }));
  const one = kind === "one" ? rows[0] : null;
  const pickupCities = [...new Set(rows.map(({ r }) => titleCaseName(r.pickup.pickup_city)).filter(Boolean))];
  const pickupNames = [...new Set(rows.map(({ r }) => titleCaseName(r.pickup.pickup_organisation || r.pickup.pickup_poc)).filter(Boolean))];
  const dropCities = [...new Set(rows.map(({ r }) => titleCaseName(r.drop.drop_city)).filter(Boolean))];
  const onePickup = pickupNames.length <= 1 && pickupCities.length <= 1;
  const checking = Boolean(preflight?.loading);
  const settled = Boolean(preflight) && !preflight.loading && !preflight.unavailable;
  const results = settled ? ids.map((id) => ({ id, res: preflight.byId?.[id] })).filter(({ res }) => res) : [];
  const blocked = results.filter(({ res }) => res.ok === false);
  const warned = results.filter(({ res }) => res.ok !== false && hasVisibleWarning(res));
  const ready = ids.length - blocked.length;
  const wallet = settled ? preflight.wallet : null;
  const chosenCourier = one ? (one.r.carrierName ? formatCarrier(one.r.carrierName).carrier : "Smart allocation") : null;
  const title = kind === "one" ? "Book this shipment?" : `Book ${ids.length} shipment${ids.length === 1 ? "" : "s"}?`;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <section ref={boxRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="book-confirm-title" className="w-full max-w-[640px] overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/80 outline-none">
        <RoadWithTrucks
          count={settled ? ready : ids.length}
          checking={checking}
          fromLabel={one ? (titleCaseName(one.r.pickup.pickup_organisation) || titleCaseName(one.r.pickup.pickup_city) || "Your pickup")
            : onePickup ? (pickupNames[0] || pickupCities[0] || "Your pickup") : `${Math.max(pickupNames.length, pickupCities.length)} pickup points`}
          fromSub={one ? [titleCaseName(one.r.pickup.pickup_city), one.r.pickup.pickup_postal_code].filter(Boolean).join(" ")
            : onePickup ? (pickupCities[0] || null) : (pickupCities.length <= 3 ? pickupCities.join(" · ") : `${pickupCities.slice(0, 2).join(" · ")} +${pickupCities.length - 2} more`)}
          toLabel={one ? (titleCaseName(one.r.drop.drop_poc) || "Customer") : `${rows.length} customer${rows.length === 1 ? "" : "s"}`}
          toSub={one ? [titleCaseName(one.r.drop.drop_city), one.r.drop.drop_postal_code].filter(Boolean).join(" ") : (dropCities.length <= 3 ? dropCities.join(" · ") : `${dropCities.slice(0, 2).join(" · ")} +${dropCities.length - 2} more`)}
        />
        <div className="px-6 pb-5 pt-4 sm:px-8">
          <h3 id="book-confirm-title" className="text-[18px] font-bold tracking-tight text-slate-900">{title}</h3>
          {one ? (
            <p className="mt-1 text-[13px] leading-relaxed text-slate-600">
              <span className="font-mono font-semibold text-slate-800">{one.r.orderRef || one.d.draft_title || one.d.draft_order_id}</span>
              {" · "}{one.r.paymentType === "COD" ? `COD ${fmtInr(one.r.amount, 2)}` : `Prepaid ${fmtInr(one.r.amount, 2)}`}
              {" · "}{chosenCourier}. The courier is booked now and the AWB comes back on this screen.
            </p>
          ) : (
            <p className="mt-1 text-[13px] leading-relaxed text-slate-600">
              Every ready draft is booked with its courier right now, one shipment each.
              {skipped > 0 && <> {skipped} with missing fields {skipped > 1 ? "are" : "is"} skipped.</>}
            </p>
          )}

          {/* Pre-flight: what the server found for this batch */}
          {preflight && !preflight.unavailable && (
            <div className="mt-4">
              {checking ? (
                <div className="flex items-center gap-2.5 rounded-xl bg-slate-50 px-3.5 py-3 text-[12.5px] text-slate-600 ring-1 ring-slate-200/70">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-[#3877FC]" />
                  Checking wallet, references and serviceability…
                </div>
              ) : (
                <>
                  <div className={`grid gap-2 ${blocked.length && warned.length ? "grid-cols-3" : blocked.length || warned.length ? "grid-cols-2" : "grid-cols-1"}`}>
                    <div className={`rounded-xl px-3.5 py-2.5 ring-1 ring-inset ${ready > 0 ? "bg-emerald-50 ring-emerald-200" : "bg-slate-50 ring-slate-200"}`}>
                      <div className={`text-[22px] font-black leading-none tabular-nums ${ready > 0 ? "text-emerald-700" : "text-slate-500"}`}>{ready}</div>
                      <div className={`mt-1 text-[10.5px] font-bold uppercase tracking-wide ${ready > 0 ? "text-emerald-700/80" : "text-slate-500"}`}>Ready to book</div>
                    </div>
                    {blocked.length > 0 && (
                      <div className="rounded-xl bg-rose-50 px-3.5 py-2.5 ring-1 ring-inset ring-rose-200">
                        <div className="text-[22px] font-black leading-none tabular-nums text-rose-700">{blocked.length}</div>
                        <div className="mt-1 text-[10.5px] font-bold uppercase tracking-wide text-rose-700/80">Blocked</div>
                      </div>
                    )}
                    {warned.length > 0 && (
                      <div className="rounded-xl bg-amber-50 px-3.5 py-2.5 ring-1 ring-inset ring-amber-200">
                        <div className="text-[22px] font-black leading-none tabular-nums text-amber-700">{warned.length}</div>
                        <div className="mt-1 text-[10.5px] font-bold uppercase tracking-wide text-amber-700/80">With warnings</div>
                      </div>
                    )}
                  </div>
                  {wallet?.checked && (
                    <div className="mt-2.5 flex flex-wrap items-center gap-x-1.5 text-[12.5px] text-slate-600">
                      <Wallet className="h-3.5 w-3.5 text-slate-400" />
                      <span>Wallet <b className="font-semibold text-slate-800">{fmtMoney(wallet.available_balance)}</b> available</span>
                      {wallet.estimated_total != null && <span className="text-slate-500">· this batch about {fmtMoney(wallet.estimated_total)}</span>}
                      {wallet.sufficient === false && <span className="font-semibold text-rose-600">· short by {fmtMoney(wallet.shortfall)}</span>}
                    </div>
                  )}
                  {(blocked.length > 0 || warned.length > 0) && (
                    <ul className="mt-2.5 max-h-40 space-y-1.5 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-2.5">
                      {[...blocked, ...warned].map(({ id, res }) => (
                        <li key={id} className="text-[12.5px] leading-snug text-slate-600">
                          <span className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle ${res.ok === false ? "bg-rose-500" : "bg-amber-400"}`} />
                          <span className="font-mono text-[11.5px] font-semibold text-slate-800 break-all">{labelFor(id)}</span>
                          <span> — {preflightReasons(res).join("; ")}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {singleBlocked && <p className="mt-2.5 text-[12.5px] text-slate-600">Fix it in the order desk, then book it from here.</p>}
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-[#FAFBFD] px-6 py-3.5 sm:px-8">
          <button type="button" onClick={onCancel} className={`${BTN_NEUTRAL} h-10 px-4 text-[13px]`}>Cancel</button>
          <button type="button" disabled={confirmDisabled} onClick={() => { onConfirm(); onCancel(); }} className={`${BTN_BLUE} h-10 px-5 text-[13px] shadow-[0_8px_18px_-10px_rgba(56,119,252,0.9)]`}>
            {singleBlocked ? <Pencil className="h-3.5 w-3.5" /> : <Truck className="h-4 w-4" strokeWidth={2.25} />} {confirmLabel}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════════════ */
export default function DraftOrdersPage() {
  const navigate = useNavigate();

  const defaultFrom = format(subMonths(new Date(), 1), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const [useDateFilter, setUseDateFilter] = useState(true);

  const [filters, setFilters] = useState({
    draftOrderId: "",
    searchKeyword: "",
    orderSource: "",
    draftTitle: "",
    tags: "",
    fromDate: defaultFrom,
    toDate: defaultTo,
  });

  const [selectedTab, setSelectedTab] = useState("DRAFT");
  const [statusCounts, setStatusCounts] = useState(Object.fromEntries(TABS.map(t => [t.key, 0])));

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalElements / limit));

  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedRows, setSelectedRows] = useState(new Set());
  const headerCheckboxRef = useRef(null);

  const [showFilters, setShowFilters] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, title: "", message: "", onConfirm: null, confirmLabel: "Confirm", variant: "danger" });
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [placingIds, setPlacingIds] = useState(new Set());
  const [bulkPlacing, setBulkPlacing] = useState(false);
  const [expandedErrorLogs, setExpandedErrorLogs] = useState({});
  const [retryingIds, setRetryingIds] = useState(new Set());

  // ── Placement revamp state. All of it is driven by what the response carries, so an old OMS leaves it idle. ──
  const [placedTicket, setPlacedTicket] = useState(null);   // { draft, placedOrder, bookedAt } → the booked ticket takes the page
  const [awbCopied, setAwbCopied] = useState(false);
  const [run, setRun] = useState(null);                     // the bulk placement being followed (PlacementRunCard)
  const runRef = useRef(null);
  runRef.current = run;
  const runTimerRef = useRef(null);
  const pollRunRef = useRef(null);
  const [runFilterId, setRunFilterId] = useState(null);     // "Show placed / failed" scopes the list to one run
  const runFilterRef = useRef(null);
  runFilterRef.current = runFilterId;
  const [preflight, setPreflight] = useState(null);         // { loading } | { unavailable } | { wallet, byId }
  const preflightSeq = useRef(0);
  const [batchPricing, setBatchPricing] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);   // when the list last loaded, for the "Updated … ago" caption

  // ─── Courier selection state ──
  const [carrierOptionsByKey, setCarrierOptionsByKey] = useState({}); // { draftOrderId: [{id,name,price,max_tat_in_days,min_tat_in_days}] }
  const [loadingCarrierKeys, setLoadingCarrierKeys] = useState(new Set());
  const [carrierSelectionByKey, setCarrierSelectionByKey] = useState({}); // { draftOrderId: carrier_partner_public_name }
  const [carrierDropdownOpenKey, setCarrierDropdownOpenKey] = useState(null);
  const [carrierDropdownPosition, setCarrierDropdownPosition] = useState({ top: 0, left: 0, width: 0, openUp: false });
  const carrierTriggerRef = useRef(null);
  const carrierMenuRef = useRef(null);

  const allOnPageSelected = useMemo(() => drafts.length > 0 && drafts.every(d => selectedRows.has(d.draft_order_id)), [drafts, selectedRows]);
  /* What the toolbar can do with the selection. Booking takes drafts and failed drafts the courier may take again
     (a failure that needs a new reference is edited first, as its row says). */
  const canBookAgain = (d) => d.draft_status === "DRAFT" || (d.draft_status === "FAILED" && d.placement_error?.retryable !== false);
  const bookableSelected = useMemo(() => drafts.filter((d) => selectedRows.has(d.draft_order_id) && canBookAgain(d)), [drafts, selectedRows]);
  // Selected drafts that can take a courier quote (the same rows that show "Get prices").
  const quotableSelected = useMemo(
    () => drafts.filter(d => selectedRows.has(d.draft_order_id) && (d.draft_status === "DRAFT" || d.draft_status === "FAILED") && quoteBlockersOf(d.order_data).length === 0),
    [drafts, selectedRows]
  );
  // Selected drafts that can be permanently deleted (DELETABLE_DRAFT_STATUSES, module scope).
  const permanentDeletableSelectedCount = useMemo(
    () => drafts.filter(d => selectedRows.has(d.draft_order_id) && DELETABLE_DRAFT_STATUSES.includes(d.draft_status)).length,
    [drafts, selectedRows]
  );
  // Count of PLACED items in the current selection - used to warn the user during bulk delete.
  const placedSelectedCount = useMemo(
    () => drafts.filter(d => selectedRows.has(d.draft_order_id) && d.draft_status === "PLACED").length,
    [drafts, selectedRows]
  );

  // ── Pre-compute validation results for all DRAFT-status orders on this page ──
  // The revamped OMS sends its own readiness verdict (`readiness.blockers`, the same checks placement runs); it wins
  // when present so the list never says "ready" for a draft the server would refuse. Without it, the client check.
  const draftValidationResults = useMemo(() => {
    const results = {};
    for (const d of drafts) {
      if (d.draft_status === "DRAFT" || d.draft_status === "FAILED") {
        const server = d.readiness;
        if (server && Array.isArray(server.blockers) && (server.ready === true || server.blockers.length > 0)) {
          results[d.draft_order_id] = server.ready === true ? [] : server.blockers.map((b) => ({
            field: b.field || b.message || String(b.code || "").toLowerCase().replace(/_/g, " "),
            section: b.section || "Order",
            message: b.message,
            code: b.code,
          }));
        } else {
          results[d.draft_order_id] = validateDraftOrderData(d.order_data);
        }
      }
    }
    return results;
  }, [drafts]);

  // Only the fields the draft search API filters on (keyword, source); the date window is always applied.
  const activeFiltersCount = useMemo(() => {
    let c = 0;
    if (filters.searchKeyword) c++;
    if (filters.orderSource) c++;
    return c;
  }, [filters]);

  const filtersRef = useRef(filters);
  const currentPageRef = useRef(currentPage);
  const limitRef = useRef(limit);
  const useDateFilterRef = useRef(useDateFilter);
  const selectedTabRef = useRef(selectedTab);
  filtersRef.current = filters;
  currentPageRef.current = currentPage;
  limitRef.current = limit;
  useDateFilterRef.current = useDateFilter;
  selectedTabRef.current = selectedTab;

  const fetchingRef = useRef(false);

  const fetchDrafts = useCallback(async (tabKey, overrides = {}) => {
    if (fetchingRef.current) return false; // a fetch is in flight; callers that must land a refresh retry once
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const merchantId = localStorage.getItem("merchantId");
      if (!localStorage.getItem("authToken") || !merchantId) return;

      const f = filtersRef.current;
      const udf = useDateFilterRef.current;

      const baseBody = {
        ...(f.orderSource && { order_source: f.orderSource }),
        ...(f.searchKeyword && { search_keyword: f.searchKeyword }),
        ...(udf && {
          from_date: formatISO(toDate(parseISO(`${f.fromDate}T00:00:00+05:30`))),
          to_date: formatISO(toDate(parseISO(`${f.toDate}T23:59:59+05:30`))),
        }),
        // Only ever set from a response that carried a run id, so it never reaches an OMS that doesn't know it.
        ...(runFilterRef.current && { placement_run_id: runFilterRef.current }),
        page: (overrides.page ?? currentPageRef.current) - 1,
        size: limitRef.current,
        sort_by: "created_date_time",
        sort_direction: "DESC",
        ...overrides,
      };

      const headers = { merchantId };

      // One tab, one status. Failed has its own tab, so Draft no longer merges failed drafts in (its count read
      // "5" with 3 drafts before).
      const body = { ...baseBody, ...(tabKey !== "all" && { draft_status: tabKey }) };
      const res = await API.post(ApiUrls.searchDraftOrders(), body, { headers });
      const list = Array.isArray(res.data.draft_orders) ? res.data.draft_orders : [];
      setDrafts(list);
      setTotalElements(res.data.total_elements ?? 0);
      setLastFetchedAt(Date.now());
      return list;
    } catch (err) {
      setError(err?.message || "Failed to fetch draft orders.");
      toast.error("Failed to fetch draft orders");
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  const fetchStatusCounts = useCallback(async (forceRefresh = false) => {
    try {
      if (!forceRefresh && _statusCountsCache && (Date.now() - _statusCountsCacheTime < STATUS_COUNTS_CACHE_TTL)) {
        setStatusCounts(_statusCountsCache);
        return;
      }

      const merchantId = localStorage.getItem("merchantId");
      if (!localStorage.getItem("authToken") || !merchantId) return;
      const statuses = ["DRAFT", "QUEUED", "PLACED", "FAILED", "DISCARDED"];
      const counts = { all: 0 };
      const f = filtersRef.current;
      const udf = useDateFilterRef.current;
      const dateFilters = udf ? {
        from_date: formatISO(toDate(parseISO(`${f.fromDate}T00:00:00+05:30`))),
        to_date: formatISO(toDate(parseISO(`${f.toDate}T23:59:59+05:30`))),
      } : {};

      // One aggregation on the revamped OMS; the five one-row searches on an OMS without it (or if it fails).
      let counted = false;
      if (!_missingEndpoints.has("status-counts")) {
        try {
          const res = await API.post(ApiUrls.draftOrderStatusCounts(), dateFilters, { headers: { merchantId } });
          const byStatus = res.data?.counts || {};
          statuses.forEach((s) => { counts[s] = Number(byStatus[s]) || 0; counts.all += counts[s]; });
          counted = true;
        } catch (err) {
          markMissing("status-counts", err);
        }
      }
      if (!counted) {
        const results = await Promise.allSettled(
          statuses.map(s => API.post(ApiUrls.searchDraftOrders(), { draft_status: s, page: 0, size: 1, ...dateFilters }, { headers: { merchantId } }))
        );
        results.forEach((r, i) => {
          const total = r.status === "fulfilled" ? (r.value.data?.total_elements ?? 0) : 0;
          counts[statuses[i]] = total;
          counts.all += total;
        });
      }

      _statusCountsCache = counts;
      _statusCountsCacheTime = Date.now();

      setStatusCounts(counts);
    } catch {
    }
  }, []);

  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (hasMountedRef.current) return;
    hasMountedRef.current = true;
    fetchDrafts(selectedTabRef.current);
    fetchStatusCounts();
  }, [fetchDrafts, fetchStatusCounts]);

  const handleTabClick = (key) => {
    setSelectedTab(key);
    setCurrentPage(1);
    setSelectedRows(new Set());
    setExpandedErrorLogs({});
    fetchDrafts(key, { page: 0 });
  };

  const handleInputChange = (e) => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFilterClick = () => {
    setCurrentPage(1);
    setSelectedRows(new Set());
    fetchDrafts(selectedTab, { page: 0 });
    fetchStatusCounts(true);
  };

  const resetFilters = () => {
    const next = { draftOrderId: "", searchKeyword: "", orderSource: "", draftTitle: "", tags: "", fromDate: defaultFrom, toDate: defaultTo };
    setFilters(next);
    setUseDateFilter(true);
    // The fetchers read these refs, which only catch up on the next render - point them at the new values now
    // so the reload below queries the reset filters, not the ones just cleared.
    filtersRef.current = next;
    useDateFilterRef.current = true;
    setRunFilterId(null);
    runFilterRef.current = null;
    setCurrentPage(1);
    setSelectedRows(new Set());
    fetchDrafts(selectedTab, { page: 0 });
    fetchStatusCounts(true);
  };

  /* Applied filters as removable chips under the toolbar (the All Shipments pattern), so the panel can stay closed. */
  const sourceLabel = (code) => Object.values(DraftOrderSourceEnum).find((s) => s.code === code)?.name || code;
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (filters.searchKeyword) chips.push({ key: "searchKeyword", label: "Keyword", value: filters.searchKeyword });
    if (filters.orderSource) chips.push({ key: "orderSource", label: "Source", value: sourceLabel(filters.orderSource) });
    if (useDateFilter && (filters.fromDate !== defaultFrom || filters.toDate !== defaultTo))
      chips.push({ key: "dateRange", label: "Created", value: `${format(parseISO(filters.fromDate), "dd MMM")} – ${format(parseISO(filters.toDate), "dd MMM")}` });
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, useDateFilter, defaultFrom, defaultTo]);
  const removeFilterChip = (key) => {
    const next = key === "dateRange"
      ? { ...filtersRef.current, fromDate: defaultFrom, toDate: defaultTo }
      : { ...filtersRef.current, [key]: "" };
    setFilters(next);
    filtersRef.current = next;
    setCurrentPage(1);
    setSelectedRows(new Set());
    fetchDrafts(selectedTab, { page: 0 });
    fetchStatusCounts(true);
  };

  /* Created-date presets: Today / 7D / 30D, same chips as All Shipments. */
  const presetRange = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - (days - 1));
    return { from: from.toLocaleDateString("en-CA"), to: to.toLocaleDateString("en-CA") };
  };
  const isPresetActive = (days) => {
    const { from, to } = presetRange(days);
    return useDateFilter && filters.fromDate === from && filters.toDate === to;
  };
  const applyDatePreset = (days) => {
    const { from, to } = presetRange(days);
    const next = { ...filtersRef.current, fromDate: from, toDate: to };
    setUseDateFilter(true);
    setFilters(next);
    filtersRef.current = next;
    useDateFilterRef.current = true;
    setCurrentPage(1);
    fetchDrafts(selectedTab, { page: 0 });
    fetchStatusCounts(true);
  };

  const toggleSelectOne = (id) => setSelectedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAllOnPage = (checked) => setSelectedRows(prev => { const n = new Set(prev); drafts.forEach(d => { checked ? n.add(d.draft_order_id) : n.delete(d.draft_order_id); }); return n; });

  // A bulk action reads the current page's selection, so a page/size change must clear it — otherwise rows the
  // merchant can no longer see stay selected and Book/Delete act on a set they cannot review.
  const prevPage = () => {
    const newPage = Math.max(1, currentPage - 1);
    setCurrentPage(newPage);
    setSelectedRows(new Set());
    fetchDrafts(selectedTab, { page: newPage - 1 });
  };
  const nextPage = () => {
    if (drafts.length < limit || currentPage >= totalPages) return;
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    setSelectedRows(new Set());
    fetchDrafts(selectedTab, { page: newPage - 1 });
  };
  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    setSelectedRows(new Set());
    fetchDrafts(selectedTab, { page: 0, size: newLimit });
  };

  /* What a single placement answered. With `placed_order` (revamped OMS) the booked ticket takes the page, exactly as
     the desks celebrate a booking; without it (old OMS, or a booking still confirming) the toasts stay as they were. */
  const consumePlaceResponse = (draft, data) => {
    const status = data?.draft_status;
    if (status === "PLACED") {
      const po = data.placed_order;
      if (po?.waybill) {
        setPlacedTicket({ draft: { ...draft, ...data }, placedOrder: po, bookedAt: new Date() });
        setAwbCopied(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (po) toast.success("Order accepted - the courier is confirming it. It shows under Booked with its AWB shortly.");
      else toast.success(`Booked. Find it under All Shipments as ${data.placed_order_summary?.order_reference_number || data.order_data?.shipment_details?.order_details?.order_reference_number || draft.order_data?.shipment_details?.order_details?.order_reference_number || "your order reference"}.`);
    } else if (status === "FAILED") {
      toast.error(`Booking failed: ${placementErrorMessage(data?.placement_error?.error_message)}`);
    } else {
      toast.info("Booking is still in progress. Refresh in a moment to see where it landed.");
    }
  };
  const placeErrorMessage = (err, fallback) => {
    const s = httpStatus(err);
    if (s === 409) return "This draft is already being booked. Give it a moment and refresh.";
    if (s === 422) return "This draft can no longer be booked - refresh to see its current state.";
    return errMessage(err, fallback);
  };

  const handlePlaceDraft = async (draftOrId) => {
    const draftId = typeof draftOrId === "string" ? draftOrId : draftOrId?.draft_order_id;
    const draft = (typeof draftOrId === "string" ? drafts.find((x) => x.draft_order_id === draftOrId) : draftOrId) || { draft_order_id: draftId };
    setPlacingIds(prev => new Set(prev).add(draftId));
    try {
      const merchantId = localStorage.getItem("merchantId");
      const res = await API.post(ApiUrls.placeDraftOrder(draftId), {}, { headers: { merchantId } });
      consumePlaceResponse(draft, res.data);
      fetchDrafts(selectedTab); fetchStatusCounts(true);
    } catch (err) {
      toast.error(placeErrorMessage(err, "Could not book this draft. Try again in a moment."));
      if ([409, 422].includes(httpStatus(err))) { fetchDrafts(selectedTab); fetchStatusCounts(true); }
    } finally { setPlacingIds(prev => { const n = new Set(prev); n.delete(draftId); return n; }); }
  };

  const handleDuplicateDraft = async (draft) => {
    try {
      const merchantId = localStorage.getItem("merchantId");
      const res = await API.post(ApiUrls.duplicateDraftOrder(draft.draft_order_id), {}, { headers: { merchantId } });
      const newDraftId = res.data?.draft_order_id;

      // Increment order_reference_number on the duplicated draft to avoid duplicates
      const od = draft.order_data || {};
      const orderRef = od.shipment_details?.order_details?.order_reference_number;
      if (newDraftId && orderRef) {
        const suffixMatch = orderRef.match(/_(\d+)$/);
        const newRef = suffixMatch
          ? orderRef.replace(/_(\d+)$/, `_${parseInt(suffixMatch[1], 10) + 1}`)
          : `${orderRef}_1`;

        const updatedOrderData = JSON.parse(JSON.stringify(od));
        updatedOrderData.shipment_details = updatedOrderData.shipment_details || {};
        updatedOrderData.shipment_details.order_details = updatedOrderData.shipment_details.order_details || {};
        updatedOrderData.shipment_details.order_details.order_reference_number = newRef;

        await API.put(ApiUrls.updateDraftOrder(newDraftId), { order_data: updatedOrderData }, { headers: { merchantId } });
      }

      toast.success(orderRef && newDraftId ? "Draft duplicated. The copy has the next order reference and sits under To book." : "Draft duplicated. Find the copy under To book.");
      fetchDrafts(selectedTab); fetchStatusCounts(true);
    } catch (err) { toast.error(errMessage(err, "Could not duplicate this draft")); }
  };

  /* ──────────────────── Bulk placement: the run card and its follow-along ──────────────────── */
  const clearRunTimer = () => { if (runTimerRef.current) { window.clearTimeout(runTimerRef.current); runTimerRef.current = null; } };
  const scheduleRunPoll = (runId, delay) => { clearRunTimer(); runTimerRef.current = window.setTimeout(() => pollRunRef.current?.(runId), delay); };
  const refreshListSoon = async () => {
    const ok = await fetchDrafts(selectedTabRef.current);
    if (ok === false) window.setTimeout(() => fetchDrafts(selectedTabRef.current), 1500); // a fetch was in flight; once more
    fetchStatusCounts(true);
  };
  // Poll GET /placement-runs/{id}: 2s → 5s → 10s while the run is RUNNING, nothing while the tab is hidden, and
  // give up (STALE, with a Refresh) after 15 minutes or five straight errors. Each tick refreshes the list only
  // when the counts moved, so a long run costs one small read per tick and nothing more.
  pollRunRef.current = async (runId) => {
    clearRunTimer();
    const cur = runRef.current;
    if (!cur || cur.runId !== runId || cur.status !== "RUNNING") return;
    if (document.hidden) { scheduleRunPoll(runId, 5000); return; }
    if (Date.now() - cur.startedAt > RUN_POLL_MAX_MS) { setRun((r) => (r && r.runId === runId ? { ...r, status: "STALE" } : r)); return; }
    try {
      const merchantId = localStorage.getItem("merchantId");
      const res = await API.get(ApiUrls.draftPlacementRun(runId), { headers: { merchantId } });
      const data = res.data || {};
      const t = data.totals || {};
      const done = data.status === "COMPLETED";
      const placed = t.placed ?? 0;
      const failed = t.failed ?? 0;
      const progressed = placed + failed !== cur.totals.placed + cur.totals.failed;
      setRun((r) => (r && r.runId === runId ? {
        ...r,
        status: done ? "COMPLETED" : "RUNNING",
        interrupted: data.interrupted === true, // closed by the server without its worker finishing
        polls: r.polls + 1,
        errors: 0,
        chunksTotal: data.chunks_total ?? r.chunksTotal,
        chunksDone: data.chunks_done ?? r.chunksDone,
        totals: { requested: t.requested ?? r.totals.requested, rejected: t.rejected ?? r.totals.rejected, queued: t.queued ?? r.totals.queued, placed, failed },
        rejected: Array.isArray(data.rejected_drafts) && data.rejected_drafts.length ? data.rejected_drafts : r.rejected,
      } : r));
      if (progressed || done) refreshListSoon();
      if (done) {
        if (data.interrupted === true) toast.warning(`Booking was interrupted on the server: ${placed} booked${failed ? `, ${failed} failed` : ""}. The rest are back in Draft.`);
        else toast[failed ? "warning" : "success"](`Booking finished: ${placed} booked${failed ? `, ${failed} failed` : ""}.`);
        return;
      }
      const polls = cur.polls + 1;
      scheduleRunPoll(runId, polls < 3 ? 2000 : polls < 8 ? 5000 : 10000);
    } catch (err) {
      const errors = cur.errors + 1;
      if (isNotFound(err) || errors >= 5) { setRun((r) => (r && r.runId === runId ? { ...r, status: "STALE", errors } : r)); return; }
      setRun((r) => (r && r.runId === runId ? { ...r, errors } : r));
      scheduleRunPoll(runId, 10000);
    }
  };
  useEffect(() => {
    const onVisibility = () => { if (!document.hidden && runRef.current?.status === "RUNNING") pollRunRef.current?.(runRef.current.runId); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (runTimerRef.current) window.clearTimeout(runTimerRef.current);
    };
  }, []);

  /* What a bulk place answered. A run id (revamped OMS) starts the follow-along; the plain response (old OMS) keeps
     the same toasts as before and a static card. */
  const consumeBulkResponse = (d, requested) => {
    const rejected = Array.isArray(d.rejected_drafts) ? d.rejected_drafts : [];
    const results = Array.isArray(d.results) ? d.results : null;
    const runId = d.run_id || null;
    const placed = results ? results.filter((x) => x.draft_status === "PLACED").length : 0;
    const failed = results ? results.filter((x) => x.draft_status === "FAILED").length : 0;
    const inBackground = d.status === "SUBMITTED" || d.status === "ACCEPTED";
    const status = inBackground ? (runId ? "RUNNING" : "ACCEPTED") : "COMPLETED";
    const next = {
      runId, status, startedAt: Date.now(), polls: 0, errors: 0, chunksTotal: null, chunksDone: null,
      totals: { requested: d.total_requested ?? requested, rejected: d.total_rejected ?? rejected.length, queued: d.total_queued ?? 0, placed, failed },
      rejected, results,
    };
    clearRunTimer();
    setRun(next);
    // A retry from a run's failed view starts a new run; keep the list scoped to the run being watched.
    if (runId && runFilterRef.current && runFilterRef.current !== runId) { setRunFilterId(runId); runFilterRef.current = runId; }
    if (status === "RUNNING") scheduleRunPoll(runId, 2000);
    else if (status === "COMPLETED") {
      if (results) toast[failed ? "warning" : "success"](`${placed} booked${failed ? `, ${failed} failed` : ""}${next.totals.rejected ? `, ${next.totals.rejected} rejected` : ""}.`);
      else toast.success(`${next.totals.queued} draft(s) booked.`);
    } else toast.info(`${next.totals.queued} draft(s) queued for booking. ${next.totals.rejected} rejected.`);
  };
  const showRunDrafts = (status) => {
    const runId = runRef.current?.runId;
    if (!runId) return;
    setRunFilterId(runId);
    runFilterRef.current = runId;
    setSelectedTab(status);
    setCurrentPage(1);
    setSelectedRows(new Set());
    setExpandedErrorLogs({});
    fetchDrafts(status, { page: 0 });
  };
  const clearRunFilter = () => {
    setRunFilterId(null);
    runFilterRef.current = null;
    setCurrentPage(1);
    setSelectedRows(new Set());
    fetchDrafts(selectedTab, { page: 0 });
  };
  const refreshRun = () => {
    const cur = runRef.current;
    if (cur?.runId) {
      setRun({ ...cur, status: "RUNNING", startedAt: Date.now(), polls: 0, errors: 0 });
      window.setTimeout(() => pollRunRef.current?.(cur.runId), 0);
    } else { fetchDrafts(selectedTab); fetchStatusCounts(true); }
  };

  /* Pre-flight (revamped OMS): what would stop each draft, in chunks of 50; `unavailable` on an OMS without it. */
  const runPreflight = async (ids, estimatedTotal) => {
    if (_missingEndpoints.has("preflight")) return { unavailable: true };
    try {
      const merchantId = localStorage.getItem("merchantId");
      const chunks = [];
      for (let i = 0; i < ids.length; i += PREFLIGHT_CHUNK) chunks.push(ids.slice(i, i + PREFLIGHT_CHUNK));
      const pages = await Promise.all(chunks.map((chunk, i) => API.post(
        ApiUrls.draftOrderPreflight(),
        { draft_order_ids: chunk, ...(i === 0 && estimatedTotal != null && { estimated_total: estimatedTotal }) },
        { headers: { merchantId } },
      )));
      const byId = {};
      pages.forEach((p) => (p.data?.drafts || []).forEach((r) => { byId[r.draft_order_id] = r; }));
      return { unavailable: false, wallet: pages[0]?.data?.wallet || null, byId };
    } catch (err) {
      markMissing("preflight", err);
      return { unavailable: true };
    }
  };
  // The batch's likely cost: each draft's chosen courier price, or the cheapest quote when it is on smart
  // allocation; unknown (null) unless every draft has been priced.
  const estimateBatchTotal = (ids) => {
    let total = 0;
    for (const id of ids) {
      const opts = carrierOptionsByKey[id];
      if (!opts || !opts.length) return null;
      const d = drafts.find((x) => x.draft_order_id === id);
      const chosen = carrierSelectionByKey[id] ?? d?.order_data?.shipment_details?.shipment_carrier_details?.carrier_partner_public_name ?? null;
      const opt = (chosen && opts.find((o) => o.name === chosen)) || opts.reduce((m, o) => (o.price < m.price ? o : m), opts[0]);
      if (opt?.price == null) return null;
      total += Number(opt.price);
    }
    return Math.round(total * 100) / 100;
  };
  const startPreflight = (ids) => {
    const token = ++preflightSeq.current;
    if (_missingEndpoints.has("preflight")) { setPreflight(null); return; }
    setPreflight({ loading: true });
    runPreflight(ids, estimateBatchTotal(ids)).then((res) => { if (preflightSeq.current === token) setPreflight({ loading: false, ...res }); });
  };
  /* How a draft is named in dialogs and toasts: its order reference, else its title, else its draft id. */
  const draftLabelOf = (d) => d?.order_data?.shipment_details?.order_details?.order_reference_number || draftTitleOf(d) || d?.draft_order_id;
  const draftLabelFor = (id) => {
    const d = drafts.find((x) => x.draft_order_id === id);
    return d ? draftLabelOf(d) : id;
  };

  const handleBulkPlace = async (explicitIds) => {
    let ids = Array.isArray(explicitIds) ? explicitIds : null;
    if (!ids) {
      // Entry without a pre-flight: every selected draft that can be booked and passes the readiness check.
      const eligibleDrafts = bookableSelected;
      if (!eligibleDrafts.length) { toast.error("Select drafts that can be booked."); return; }
      const invalid = eligibleDrafts.filter((d) => (draftValidationResults[d.draft_order_id] || validateDraftOrderData(d.order_data)).length > 0);
      ids = eligibleDrafts.filter((d) => !invalid.includes(d)).map((d) => d.draft_order_id);
      if (ids.length === 0) { toast.error(`All ${invalid.length} selected draft${invalid.length > 1 ? "s have" : " has"} missing fields. Complete them before booking.`); return; }
      if (invalid.length > 0) toast.info(`${invalid.length} draft${invalid.length > 1 ? "s" : ""} with missing fields skipped. Booking ${ids.length}.`);
    }
    if (!ids.length) { toast.error("Nothing to book."); return; }

    setBulkPlacing(true);
    try {
      const merchantId = localStorage.getItem("merchantId");
      const res = await API.post(ApiUrls.bulkPlaceDraftOrders(), { draft_order_ids: ids }, { headers: { merchantId } });
      consumeBulkResponse(res.data || {}, ids.length);
      setSelectedRows(new Set()); fetchDrafts(selectedTab); fetchStatusCounts(true);
    } catch (err) { toast.error(errMessage(err, "Could not start the booking. Nothing was booked; try again in a moment.")); }
    finally { setBulkPlacing(false); }
  };
  const retryRunFailures = async () => {
    const runId = runRef.current?.runId;
    if (!runId) return;
    try {
      const merchantId = localStorage.getItem("merchantId");
      const res = await API.post(ApiUrls.searchDraftOrders(), { placement_run_id: runId, draft_status: "FAILED", page: 0, size: 200 }, { headers: { merchantId } });
      const ids = (res.data?.draft_orders || []).filter((d) => d.placement_error?.retryable !== false).map((d) => d.draft_order_id);
      if (!ids.length) { toast.info("None of the failed drafts can be retried as they are - edit them first."); return; }
      await handleBulkPlace(ids);
    } catch (err) { toast.error(err?.message || "Could not load the failed drafts"); }
  };

  /**
   * Permanently delete a draft order — a direct delete, no discard step. The server verifies the draft belongs to
   * this merchant and is in a deletable status (DRAFT / FAILED / DISCARDED); a draft being booked or already booked
   * is refused. There is no recovery.
   */
  const handlePermanentDeleteDraft = async (draftId, currentStatus) => {
    if (!DELETABLE_DRAFT_STATUSES.includes(currentStatus)) {
      toast.error(currentStatus === "PLACED" ? "Booked shipments cannot be deleted." : "This draft cannot be deleted right now.");
      return;
    }
    try {
      const merchantId = localStorage.getItem("merchantId");
      await API.delete(ApiUrls.permanentDeleteDraftOrder(draftId), { headers: { merchantId } });
      toast.success("Draft deleted.");
      fetchDrafts(selectedTab); fetchStatusCounts(true);
    } catch (err) {
      toast.error(errMessage(err, "Could not delete this draft"));
    }
  };

  /**
   * Bulk permanently delete selected drafts — direct deletes, one per draft. Only deletable statuses are attempted;
   * booked or being-booked drafts are skipped. Reports a per-item success / skipped / failed summary.
   */
  const handleBulkPermanentDelete = async () => {
    const selectedDrafts = Array.from(selectedRows)
      .map(id => drafts.find(d => d.draft_order_id === id))
      .filter(Boolean);
    const skippedCount = selectedDrafts.filter(d => !DELETABLE_DRAFT_STATUSES.includes(d.draft_status)).length;
    const eligible = selectedDrafts.filter(d => DELETABLE_DRAFT_STATUSES.includes(d.draft_status));

    if (!eligible.length) {
      toast.error("No deletable drafts selected. Booked or being-booked drafts cannot be deleted.");
      return;
    }

    const merchantId = localStorage.getItem("merchantId");
    let success = 0;
    const failures = [];

    for (const d of eligible) {
      try {
        await API.delete(ApiUrls.permanentDeleteDraftOrder(d.draft_order_id), { headers: { merchantId } });
        success++;
      } catch (err) {
        failures.push({ id: d.draft_order_id, reason: errMessage(err, "Unknown error") });
      }
    }

    setSelectedRows(new Set());
    fetchDrafts(selectedTab);
    fetchStatusCounts(true);

    const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
    if (failures.length === 0 && skippedCount === 0) {
      toast.success(`${plural(success, "draft")} deleted.`);
    } else if (failures.length === 0 && skippedCount > 0) {
      toast.info(`${plural(success, "draft")} deleted. ${plural(skippedCount, "draft")} skipped: booked or being-booked drafts cannot be deleted.`);
    } else {
      toast.error(`${success} deleted, ${failures.length} could not be deleted${skippedCount > 0 ? `, ${skippedCount} skipped` : ""}.`);
    }
  };

  /* The upload answers with what it made of the file; say so, especially when it made nothing, or when the
     request itself failed (the modal swallows the rejection, so a silent failure would leave the merchant guessing). */
  const handleBulkUpload = async (file) => {
    const merchantId = localStorage.getItem("merchantId");
    const fd = new FormData(); fd.append("file", file);
    let res;
    try {
      res = await API.post(ApiUrls.bulkUploadDraftOrders(), fd, { headers: { merchantId, "Content-Type": "multipart/form-data" } });
    } catch (err) {
      toast.error(errMessage(err, "Could not upload the CSV. Check the file and try again."));
      throw err;
    }
    const d = res.data || {};
    const created = Number(d.total_drafts_created) || 0;
    const failed = Number(d.total_failed) || 0;
    const skipped = Number(d.total_skipped) || 0;
    const firstReason = d.failures?.[0]?.reason || d.skipped_orders?.[0]?.reason || null;
    const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
    if (created === 0) {
      toast.error(failed + skipped > 0
        ? `No drafts created: ${plural(failed + skipped, "row")} could not be read${firstReason ? ` (${firstReason})` : ""}. Check the file against the sample CSV.`
        : "No drafts were created from this file. Check it against the sample CSV.");
    } else if (failed + skipped > 0) {
      toast.warning(`${plural(created, "draft")} created. ${plural(failed + skipped, "row")} skipped${firstReason ? `: ${firstReason}` : ""}.`);
    } else {
      toast.success(`${plural(created, "draft")} created from the CSV. They sit under To book.`);
    }
    fetchDrafts(selectedTab); fetchStatusCounts(true);
  };

  const handleRetryPlace = async (draft) => {
    const draftId = draft.draft_order_id;
    setRetryingIds(prev => new Set(prev).add(draftId));
    try {
      const merchantId = localStorage.getItem("merchantId");
      const res = await API.post(ApiUrls.placeDraftOrder(draftId), {}, { headers: { merchantId } });
      consumePlaceResponse(draft, res.data);
      fetchDrafts(selectedTab); fetchStatusCounts(true);
    } catch (err) {
      toast.error(placeErrorMessage(err, "Could not retry the booking. Try again in a moment."));
      if ([409, 422].includes(httpStatus(err))) { fetchDrafts(selectedTab); fetchStatusCounts(true); }
    } finally {
      setRetryingIds(prev => { const n = new Set(prev); n.delete(draftId); return n; });
    }
  };

  /* ──────────────────── Courier selection - dropdown positioning ──────────────────── */
  const DROPDOWN_MAX_HEIGHT = 400; // header + Smart allocation + up to ~6 courier rows before the list scrolls
  const computeCarrierDropdownPosition = useCallback(() => {
    if (!carrierTriggerRef.current) return;
    const rect = carrierTriggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > DROPDOWN_MAX_HEIGHT;
    const width = Math.max(rect.width, 280);
    // Clamp left so dropdown doesn't overflow viewport
    const left = Math.min(rect.left, window.innerWidth - width - 8);
    setCarrierDropdownPosition({
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
      left: Math.max(8, left),
      width,
      openUp,
    });
  }, []);

  useLayoutEffect(() => {
    if (carrierDropdownOpenKey == null) return;
    requestAnimationFrame(computeCarrierDropdownPosition);
    const handleUpdate = () => requestAnimationFrame(computeCarrierDropdownPosition);
    window.addEventListener("resize", handleUpdate);
    return () => { window.removeEventListener("resize", handleUpdate); };
  }, [carrierDropdownOpenKey, computeCarrierDropdownPosition]);

  // Close courier dropdown on click outside OR on external scroll (not inside the dropdown)
  useEffect(() => {
    if (carrierDropdownOpenKey == null) return;
    const onMouseDown = (e) => {
      const inRoot = e.target.closest(`[data-carrier-dropdown-root="${carrierDropdownOpenKey}"]`);
      const inMenu = carrierMenuRef.current && carrierMenuRef.current.contains(e.target);
      if (!inRoot && !inMenu) setCarrierDropdownOpenKey(null);
    };
    const onScroll = (e) => {
      if (carrierMenuRef.current && carrierMenuRef.current.contains(e.target)) return;
      setCarrierDropdownOpenKey(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("scroll", onScroll, true);
    return () => { document.removeEventListener("mousedown", onMouseDown); document.removeEventListener("scroll", onScroll, true); };
  }, [carrierDropdownOpenKey]);

  /* ──────────────────── Courier selection - fetch carrier options ──────────────────── */
  /* One quote context per distinct lane (pincodes, type, payment, value, weight, dims): drafts that share a lane
     share the answer, and the batch endpoint takes up to 50 contexts per call. The per-row "Get prices" is the
     single-draft case of the same call. */
  const quoteContextFor = (draft) => {
    const od = draft.order_data || {};
    const pickup = od.pickup_details || {};
    const drop = od.drop_details || {};
    const sd = od.shipment_details || {};
    const orderDet = sd.order_details || {};
    const firstPkg = (sd.shipment_packages || orderDet.shipment_packages || [])[0] || {};
    if (quoteBlockersOf(od).length > 0) return null; // pincodes, weight, dimensions, payment mode: a quote needs all of them
    const context = {
      shipment_type: od.shipment_order_type || "FORWARD",
      payment_mode: orderDet.order_type || null,
      invoice_value: orderDet.order_invoice_details?.invoice_value || 0,
      rvp_with_quality_check: false,
      weight_in_grams: Number(firstPkg.weight?.value || 0),
      length_in_cm: Number(firstPkg.dimensions?.length || 0),
      breadth_in_cm: Number(firstPkg.dimensions?.breadth || 0),
      height_in_cm: Number(firstPkg.dimensions?.height || 0),
      pickup_pincode: pickup.pickup_postal_code,
      drop_pincode: drop.drop_postal_code,
    };
    return { key: JSON.stringify(context), context };
  };
  // Serviceable lanes only, one per courier name (the first occurrence wins).
  const parseCarrierOptions = (result) => {
    const map = result?.carrier_options_calculation_result_by_carrier_partner_id || {};
    const seenNames = new Set();
    return Object.entries(map)
      .filter(([, val]) => val && val.serviceable === true)
      .map(([id, val]) => ({
        id,
        name: val.carrier_partner_public_name,
        price: val.total_amount_to_charge_after_tax,
        max_tat_in_days: val.max_tat_in_days,
        min_tat_in_days: val.min_tat_in_days,
      }))
      .filter((opt) => {
        if (seenNames.has(opt.name)) return false;
        seenNames.add(opt.name);
        return true;
      });
  };

  const fetchCourierOptionsForDrafts = async (draftList, { single = false } = {}) => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) { toast.error("Auth token missing. Please sign in again."); return; }

    let allocatedCarriers = [];
    try { allocatedCarriers = JSON.parse(localStorage.getItem("allocatedCarriers") || "[]") || []; }
    catch { toast.error("Carrier configuration is invalid."); return; }
    if (!Array.isArray(allocatedCarriers) || allocatedCarriers.length === 0) {
      toast.error("No allocated carriers found. Please configure carriers first."); return;
    }
    const carrierIdsToEvaluate = allocatedCarriers.map(c => c.carrier_partner_id);

    const lanes = new Map();
    const skipped = [];
    for (const draft of draftList) {
      const q = quoteContextFor(draft);
      if (!q) { skipped.push(draft.draft_order_id); continue; }
      if (!lanes.has(q.key)) lanes.set(q.key, { correlationId: uuidv4(), context: q.context, draftIds: [] });
      lanes.get(q.key).draftIds.push(draft.draft_order_id);
    }
    if (single && skipped.length) { toast.error(quoteBlockersLabel(quoteBlockersOf(draftList[0]?.order_data)) || "This draft cannot be priced yet."); return; }
    const entries = Array.from(lanes.values());
    if (!entries.length) { toast.error("None of the selected drafts has the pincodes, weight, dimensions and payment mode a price needs."); return; }

    const loadingIds = entries.flatMap((e) => e.draftIds);
    setLoadingCarrierKeys(prev => new Set([...prev, ...loadingIds]));
    if (!single) setBatchPricing(true);

    try {
      const chunks = [];
      for (let i = 0; i < entries.length; i += 50) chunks.push(entries.slice(i, i + 50));
      const responses = await Promise.all(chunks.map((chunk) => axios.post(
        ApiUrls.retrieveCarrierOptionsInBatch(),
        chunk.map((e) => ({ correlation_id: e.correlationId, carrier_options_calculation_context: e.context, carrier_partner_ids_to_evaluate: carrierIdsToEvaluate })),
        { headers: { Authorization: authToken, "Content-Type": "application/json" } },
      )));

      const next = {};
      let quoted = 0;
      let unserviceable = 0;
      chunks.forEach((chunk, ci) => {
        const rows = Array.isArray(responses[ci]?.data) ? responses[ci].data : [];
        const byCorrelation = new Map(rows.map((row) => [row.correlation_id, row]));
        chunk.forEach((e, ei) => {
          // Matched by correlation id; by position if the response has none.
          const row = byCorrelation.get(e.correlationId) || (rows.length === chunk.length ? rows[ei] : null);
          const options = parseCarrierOptions(row);
          e.draftIds.forEach((id) => { next[id] = options; });
          if (options.length) quoted += e.draftIds.length; else unserviceable += e.draftIds.length;
        });
      });
      // No silent pre-selection: what the row shows is what the draft has saved. The menu tags the
      // cheapest lane; picking it saves it (handleCourierSelect).
      setCarrierOptionsByKey(prev => ({ ...prev, ...next }));

      if (single) {
        if (unserviceable) toast.error("No serviceable courier options found for this draft.");
      } else {
        const parts = [`Prices fetched for ${quoted} draft${quoted === 1 ? "" : "s"}`];
        if (unserviceable) parts.push(`${unserviceable} not serviceable`);
        if (skipped.length) parts.push(`${skipped.length} without package details`);
        toast[quoted ? "success" : "info"](parts.join(" · "));
      }
    } catch (err) {
      toast.error(errMessage(err, "Could not fetch courier prices. Try again in a moment."));
    } finally {
      setLoadingCarrierKeys(prev => { const n = new Set(prev); loadingIds.forEach((id) => n.delete(id)); return n; });
      if (!single) setBatchPricing(false);
    }
  };
  const performCourierFetchForDraft = (draft) => fetchCourierOptionsForDrafts([draft], { single: true });

  /* ──────────────────── Courier selection - update draft with chosen carrier ──────────────────── */
  const handleCourierSelect = async (draftId, carrierPublicName) => {
    setCarrierDropdownOpenKey(null);
    // The chosen courier is shown only after the save succeeds: an optimistic value left on a failed PUT would
    // make the row and the wallet estimate claim a courier and price the draft does not actually carry.
    const previousSelection = carrierSelectionByKey[draftId];

    // Update draft order_data with shipment_carrier_details.carrier_partner_public_name
    const draft = drafts.find(d => d.draft_order_id === draftId);
    const currentOrderData = draft?.order_data || {};
    const updatedShipmentDetails = { ...(currentOrderData.shipment_details || {}) };
    if (carrierPublicName) {
      updatedShipmentDetails.shipment_carrier_details = {
        carrier_partner_public_name: carrierPublicName,
      };
    } else {
      delete updatedShipmentDetails.shipment_carrier_details;
    }
    const updatedOrderData = { ...currentOrderData, shipment_details: updatedShipmentDetails };

    try {
      const merchantId = localStorage.getItem("merchantId");
      await API.put(ApiUrls.updateDraftOrder(draftId), { order_data: updatedOrderData }, { headers: { merchantId } });
      setCarrierSelectionByKey(prev => ({ ...prev, [draftId]: carrierPublicName }));
      toast.success(carrierPublicName ? "Courier preference saved" : "Courier preference cleared");
      // Update local draft data to reflect the change
      setDrafts(prev => prev.map(d => d.draft_order_id === draftId ? { ...d, order_data: updatedOrderData } : d));
    } catch (err) {
      // Leave the previously shown selection as it was; do not strand a courier the draft never saved.
      setCarrierSelectionByKey(prev => ({ ...prev, [draftId]: previousSelection }));
      toast.error(errMessage(err, "Could not save the courier. Try again in a moment."));
    }
  };

  /**
   * Flatten draft order_data (nested pickup_details/drop_details/shipment_details)
   * into the flat structure that CreateForwardOrder/CreateReverseOrder cloneFrom expects.
   */
  const normalizeDraftOrderData = (od) => {
    if (!od) return {};
    const pickup = od.pickup_details || {};
    const drop = od.drop_details || {};
    const sd = od.shipment_details || {};
    const orderDet = sd.order_details || {};
    // Packages can sit under shipment_details or order_details depending on the source; the same fallback the
    // row, the validator and the quote all use. Reading only the first path dropped the package on reopen.
    const pkgs = sd.shipment_packages || orderDet.shipment_packages || [];
    const firstPkg = pkgs[0] || {};

    return {
      shipment_order_type: od.shipment_order_type,
      // Flat pickup fields
      pickup_poc: pickup.pickup_poc,
      pickup_name: pickup.pickup_poc,
      pickup_organisation: pickup.pickup_organisation,
      pickup_address: pickup.pickup_address,
      pickup_district: pickup.pickup_district,
      pickup_city: pickup.pickup_city,
      pickup_state: pickup.pickup_state,
      pickup_postal_code: pickup.pickup_postal_code,
      pickup_pincode: pickup.pickup_postal_code,
      pickup_landmark: pickup.pickup_landmark,
      pickup_poc_email: pickup.pickup_poc_email,
      pickup_poc_contact_number: pickup.pickup_poc_contact_number,
      pickup_country_code: pickup.pickup_country_code,
      pickup_address_type: pickup.pickup_address_type,
      gst_number: pickup.gst_number,
      // Flat drop fields
      drop_name: drop.drop_poc,
      drop_poc: drop.drop_poc,
      drop_organisation: drop.drop_organisation,
      drop_address: drop.drop_address,
      drop_city: drop.drop_city,
      drop_state: drop.drop_state,
      drop_postal_code: drop.drop_postal_code,
      drop_pincode: drop.drop_postal_code,
      drop_poc_email: drop.drop_poc_email,
      drop_poc_contact_number: drop.drop_poc_contact_number,
      drop_country_code: drop.drop_country_code,
      drop_landmark: drop.drop_landmark,
      // Order details
      order_type: orderDet.order_type,
      order_reference_number: orderDet.order_reference_number,
      invoice_number: orderDet.order_invoice_details?.invoice_number,
      // Payment
      payment_details: {
        payment_mode: orderDet.order_type,
        total_amount: (orderDet.order_type === "COD" ? (orderDet.cod_amount_details?.value ?? orderDet.cod_amount_details?.cod_amount) : null) || orderDet.order_invoice_details?.invoice_value,
      },
      // Packages - use shipment_packages at top level like search-orders API
      shipment_packages: pkgs,
      // Package dimensions for the first package
      shipment_package_dimensions: firstPkg.dimensions || {},
      dead_weight_in_grams: firstPkg.weight?.value,
      product_weight: firstPkg.weight,
    };
  };

  /* Open a draft in its order desk, pre-filled. A failed draft the courier refused for good cannot be booked again
     under the same order reference (create-order answers the same refusal), so the desk opens with the next "_n"
     reference. A failure the courier may take again keeps its reference: the retry resumes that same order instead
     of creating a second one. */
  const goToDeskWith = (d) => {
    const normalized = normalizeDraftOrderData(d.order_data);
    const needsNewReference = d.draft_status === "FAILED" && d.placement_error?.retryable !== true;
    if (needsNewReference && normalized.order_reference_number) {
      const ref = normalized.order_reference_number;
      const suffixMatch = ref.match(/_(\d+)$/);
      normalized.order_reference_number = suffixMatch ? ref.replace(/_(\d+)$/, `_${parseInt(suffixMatch[1], 10) + 1}`) : `${ref}_1`;
    }
    // A return draft opens in the reverse desk; the type is "RVP" from the reverse desk, "REVERSE" from older data.
    const isReverse = isReverseType(d.order_data?.shipment_order_type);
    navigate(isReverse ? "/create-reverse-order" : "/create-forward-order", { state: { cloneFrom: normalized, draftOrderId: d.draft_order_id } });
  };

  const handleActionClick = (action, draft) => {
    switch (action) {
      case "view":
        setExpandedErrorLogs((prev) => ({ ...prev, [draft.draft_order_id]: !prev[draft.draft_order_id] }));
        break;
      case "place": {
        // The same verdict the row showed (server readiness wins); re-validating fresh here would let a row read
        // "Ready to book" and then answer the click with "not ready" when the two checks disagree.
        const valErrors = draftValidationResults[draft.draft_order_id] || validateDraftOrderData(draft.order_data);
        if (valErrors.length > 0) {
          const grouped = valErrors.reduce((acc, e) => { (acc[e.section] = acc[e.section] || []).push(e.field); return acc; }, {});
          setConfirmModal({
            open: true,
            title: "This draft isn't ready to book",
            message: (
              <div className="text-left">
                <p>
                  <span className="font-semibold text-slate-800">{draft.draft_title || draft.draft_order_id}</span> is missing {valErrors.length} required field{valErrors.length > 1 ? "s" : ""}.
                </p>
                <div className="mt-3 max-h-56 space-y-2.5 overflow-y-auto rounded-xl border border-amber-200 bg-amber-50/70 px-3.5 py-3">
                  {Object.entries(grouped).map(([section, fields]) => (
                    <div key={section}>
                      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-amber-700">{section}</div>
                      <ul className="mt-1 space-y-0.5">
                        {fields.map((f) => (
                          <li key={f} className="flex items-start gap-1.5 text-[13px] text-slate-700">
                            <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[12.5px] text-slate-500">Open the draft in the order desk to fill these in, then book it from here.</p>
              </div>
            ),
            onConfirm: () => goToDeskWith(draft),
            confirmLabel: "Edit draft",
            variant: "danger",
          });
        } else {
          openSinglePlaceConfirm(draft);
        }
        break;
      }
      case "retry":
        setConfirmModal({ open: true, title: "Retry booking this draft?", message: `${draftLabelOf(draft)} is sent to the courier again with the same details.`, onConfirm: () => handleRetryPlace(draft), confirmLabel: "Retry booking", variant: "info" });
        break;
      case "edit":
        goToDeskWith(draft);
        break;
      case "duplicate":
        handleDuplicateDraft(draft);
        break;
      case "viewShipment": {
        // The shipment page loads by the merchant's order reference, which the draft was booked under. The OMS
        // shipment id is internal and never reaches the merchant UI, not even as a URL.
        const target = draft.placed_order_summary?.order_reference_number
          || draft.order_data?.shipment_details?.order_details?.order_reference_number;
        if (target) {
          window.open(`${window.location.origin}/shipment-details/${encodeURIComponent(target)}`, "_blank", "noopener,noreferrer");
        } else {
          toast.info("This booking has no order reference to open. Find it under All Shipments by its AWB.");
        }
        break;
      }
      case "permanentDelete": {
        if (!DELETABLE_DRAFT_STATUSES.includes(draft.draft_status)) {
          toast.error(draft.draft_status === "PLACED" ? "Booked shipments cannot be deleted." : "This draft cannot be deleted right now.");
          break;
        }
        setConfirmModal({
          open: true,
          title: "Delete this draft?",
          message: (
            <div className="space-y-2">
              <p>Delete <b>{draftLabelOf(draft)}</b>? The draft and its data are removed for good.</p>
              <p className="text-[12.5px] font-medium text-rose-700">This cannot be undone.</p>
            </div>
          ),
          onConfirm: () => handlePermanentDeleteDraft(draft.draft_order_id, draft.draft_status),
          confirmLabel: "Delete",
          variant: "danger",
        });
        break;
      }
      default: break;
    }
  };

  /* ══════════════════════════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════════════════════════ */
  /* Bulk-action confirms, opened from the toolbar buttons. */
  /* Both Place confirms carry a pre-flight (`preflight: { kind, ids }`): the dialog is live, so the answer refines
     the count and the action once it lands (see the confirm* values computed before render). */
  const openSinglePlaceConfirm = (draft) => {
    const id = draft.draft_order_id;
    const ref = draftLabelOf(draft);
    startPreflight([id]);
    setConfirmModal({
      open: true,
      title: "Book this shipment?",
      message: `${ref} becomes a live shipment and the courier is booked now.`,
      onConfirm: () => handlePlaceDraft(draft),
      confirmLabel: "Book shipment",
      variant: "info",
      preflight: { kind: "one", ids: [id], draft, drafts: [draft] },
    });
  };
  const openBulkPlace = () => {
    const eligible = bookableSelected;
    const invalid = eligible.filter((d) => (draftValidationResults[d.draft_order_id] || []).length > 0);
    const validIds = eligible.filter((d) => !invalid.includes(d)).map((d) => d.draft_order_id);
    if (validIds.length === 0) { toast.error(`All ${invalid.length} selected draft(s) have missing mandatory fields. Complete them first.`); return; }
    startPreflight(validIds);
    setConfirmModal({
      open: true,
      title: "Book these drafts as shipments",
      message: (
        <>
          Each ready draft becomes a live shipment and its courier is booked now.
          {invalid.length > 0 && <> {invalid.length} with missing fields {invalid.length > 1 ? "are" : "is"} skipped.</>}
        </>
      ),
      onConfirm: () => handleBulkPlace(validIds),
      confirmLabel: `Book ${validIds.length}`,
      variant: "info",
      preflight: { kind: "bulk", ids: validIds, drafts: eligible.filter((d) => !invalid.includes(d)), skipped: invalid.length },
    });
  };
  const openBulkDelete = () => {
    if (permanentDeletableSelectedCount === 0) { toast.error(placedSelectedCount > 0 ? "Booked shipments cannot be deleted." : "Select drafts to delete."); return; }
    const skipped = selectedRows.size - permanentDeletableSelectedCount;
    setConfirmModal({
      open: true,
      title: `Delete ${permanentDeletableSelectedCount} draft${permanentDeletableSelectedCount > 1 ? "s" : ""}?`,
      message: (
        <div className="space-y-2">
          <p>Delete <b>{permanentDeletableSelectedCount}</b> draft{permanentDeletableSelectedCount > 1 ? "s" : ""} for good? The drafts and their data are removed.</p>
          {skipped > 0 && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">{skipped} in your selection {skipped > 1 ? "are" : "is"} skipped: booked or being-booked drafts cannot be deleted.</p>}
          <p className="text-[12.5px] font-medium text-rose-700">This cannot be undone.</p>
        </div>
      ),
      onConfirm: handleBulkPermanentDelete,
      confirmLabel: `Delete ${permanentDeletableSelectedCount}`,
      variant: "danger",
    });
  };

  /* Refresh: one reload, then a 20-second cooldown drained by a bar under the button (the All Shipments recipe, shorter
     because a draft list is a cheap query). The 1 s tick behind it runs only while cooling and never makes a request. */
  const REFRESH_COOLDOWN_MS = 20 * 1000;
  const [refreshAvailableAt, setRefreshAvailableAt] = useState(0);
  const [cooldownNow, setCooldownNow] = useState(() => Date.now());
  useEffect(() => {
    if (!refreshAvailableAt) return undefined;
    let t = null;
    const tick = () => {
      const now = Date.now();
      setCooldownNow(now);
      if (now >= refreshAvailableAt && t) window.clearInterval(t);
    };
    t = window.setInterval(tick, 1000);
    tick();
    return () => window.clearInterval(t);
  }, [refreshAvailableAt]);
  const cooldownLeftMs = Math.max(0, refreshAvailableAt - cooldownNow);
  const refreshCooling = cooldownLeftMs > 0;
  const cooldownLeftS = Math.ceil(cooldownLeftMs / 1000);
  // How fresh the list is: "Updated just now / 3 min ago" beside Refresh, re-rendered on a 30 s tick (a timer, never a
  // request). After a manual refresh the caption answers with what changed on this page instead of a silent swap.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNowTick(Date.now()), 30000);
    return () => window.clearInterval(t);
  }, []);
  const updatedAgo = (() => {
    if (!lastFetchedAt) return null;
    const s = Math.max(0, Math.round((nowTick - lastFetchedAt) / 1000));
    if (s < 60) return "just now";
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min ago`;
    return `${Math.round(m / 60)} h ago`;
  })();
  const [refreshSummary, setRefreshSummary] = useState(null);
  const refreshSummaryTimer = useRef(null);
  useEffect(() => () => { if (refreshSummaryTimer.current) window.clearTimeout(refreshSummaryTimer.current); }, []);
  const rowSignature = (d) => `${d.draft_status ?? ""}|${d.updated_date_time ?? ""}|${d.placed_order_summary?.waybill ?? ""}`;
  const handleRefresh = async () => {
    if (loading || refreshCooling) return;
    setRefreshAvailableAt(Date.now() + REFRESH_COOLDOWN_MS);
    const before = new Map(drafts.map((d) => [d.draft_order_id, rowSignature(d)]));
    fetchStatusCounts(true);
    const next = await fetchDrafts(selectedTab);
    if (!Array.isArray(next)) return; // skipped or failed; the toast already said so
    let updated = 0;
    let added = 0;
    const changedIds = [];
    next.forEach((d) => {
      if (!before.has(d.draft_order_id)) added += 1;
      else if (before.get(d.draft_order_id) !== rowSignature(d)) { updated += 1; changedIds.push(d.draft_order_id); }
    });
    // A changed draft (edited elsewhere) may have a new weight/lane, so its cached courier prices are stale: drop them.
    if (changedIds.length) setCarrierOptionsByKey((prev) => { const n = { ...prev }; changedIds.forEach((id) => delete n[id]); return n; });
    setRefreshSummary({ updated, added });
    if (refreshSummaryTimer.current) window.clearTimeout(refreshSummaryTimer.current);
    refreshSummaryTimer.current = window.setTimeout(() => setRefreshSummary(null), 5000);
  };
  const refreshSummaryLabel = refreshSummary
    ? (refreshSummary.updated === 0 && refreshSummary.added === 0
      ? "No changes"
      : [refreshSummary.updated ? `${refreshSummary.updated} updated` : null, refreshSummary.added ? `${refreshSummary.added} new` : null].filter(Boolean).join(" · "))
    : null;
  const tabCount = (key) => statusCounts[key] ?? 0;
  const toggleRow = (id) => setExpandedErrorLogs((prev) => ({ ...prev, [id]: !prev[id] }));
  /* The Actions column pins to the right only while the table actually overflows (All Shipments' hasHScroll). */
  const scrollRef = useRef(null);
  const [hasHScroll, setHasHScroll] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const check = () => setHasHScroll(el.scrollWidth > el.clientWidth + 1);
    check();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [drafts, loading]);

  /* The Place confirms are live: the pre-flight answer shrinks the count, disables a confirm that would place
     nothing, or turns a single blocked draft's button into "Edit draft". Old OMS (no pre-flight): unchanged. */
  const pf = confirmModal.preflight || null;
  const pfSettled = Boolean(pf) && Boolean(preflight) && !preflight.loading && !preflight.unavailable;
  const pfReadyIds = pf ? (pfSettled ? pf.ids.filter((id) => preflight.byId?.[id]?.ok !== false) : pf.ids) : null;
  const singleBlocked = pf?.kind === "one" && pfSettled && pfReadyIds.length === 0;
  const confirmLabelLive = !pf ? confirmModal.confirmLabel : pf.kind === "bulk" ? `Book ${pfReadyIds.length}` : singleBlocked ? "Edit draft" : confirmModal.confirmLabel;
  const confirmDisabledLive = Boolean(pf) && (Boolean(preflight?.loading) || (pf.kind === "bulk" && pfReadyIds.length === 0));
  const confirmActionLive = !pf ? confirmModal.onConfirm : pf.kind === "bulk" ? () => handleBulkPlace(pfReadyIds) : singleBlocked ? () => goToDeskWith(pf.draft) : confirmModal.onConfirm;
  const closeConfirm = () => {
    setConfirmModal({ open: false, title: "", message: "", onConfirm: null, confirmLabel: "Confirm", variant: "danger" });
    setPreflight(null);
    preflightSeq.current += 1; // a pre-flight still in flight must not repaint a closed dialog
  };

  // No overflow clipping on the page wrappers or the card: any ancestor with overflow other than visible becomes the
  // sticky containing block, which is exactly why the tab strip on All Shipments never sticks.
  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-50 pb-8">
      <style>{`
        @keyframes slide-down { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scale-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-slide-down { animation: slide-down 0.3s ease-out; }
        .animate-scale-in { animation: scale-in 0.2s ease-out; }
        /* The trucks on the booking dialog: a nervous idle while the checks run, a settled bob once they can leave. */
        @keyframes dro-truck-idle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
        @keyframes dro-truck-ready { 0% { transform: translateX(-6px) scale(0.92); opacity: 0; } 60% { transform: translateX(2px) scale(1.02); opacity: 1; } 100% { transform: translateX(0) scale(1); opacity: 1; } }
        .dro-truck-idle { animation: dro-truck-idle 1.1s ease-in-out infinite; }
        .dro-truck-ready { animation: dro-truck-ready 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
        @media (prefers-reduced-motion: reduce) { .animate-slide-down, .animate-scale-in, .dro-truck-idle, .dro-truck-ready { animation: none; } }
        /* The MUI date-range picker, compacted to the h-9 input recipe (same rules as All Shipments). */
        .date-input-wrapper .MuiPaper-root {
          min-height: 2.25rem !important;
          height: 2.25rem;
          padding: 0 0.75rem !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 0.5rem !important;
          box-shadow: none !important;
          justify-content: space-between;
          transition: border-color 150ms;
          cursor: pointer;
        }
        .date-input-wrapper .MuiPaper-root .MuiTypography-root {
          font-size: 0.875rem !important;
          font-weight: 400 !important;
          font-family: "Inter", sans-serif !important;
          color: #1e293b !important;
          letter-spacing: 0 !important;
        }
        .date-input-wrapper .MuiPaper-root .MuiSvgIcon-root {
          font-size: 1rem !important;
          color: #94a3b8 !important;
          margin-left: 0.5rem !important;
        }
      `}</style>
      {pf ? (
        <BookConfirmModal
          open={confirmModal.open}
          kind={pf.kind}
          drafts={pf.drafts || (pf.draft ? [pf.draft] : [])}
          skipped={pf.skipped || 0}
          preflight={preflight}
          ids={pf.ids}
          labelFor={draftLabelFor}
          onConfirm={confirmActionLive}
          onCancel={closeConfirm}
          confirmLabel={confirmLabelLive}
          confirmDisabled={confirmDisabledLive}
          singleBlocked={singleBlocked}
        />
      ) : (
        <ConfirmModal
          open={confirmModal.open}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmActionLive}
          onCancel={closeConfirm}
          confirmLabel={confirmLabelLive}
          confirmDisabled={confirmDisabledLive}
          variant={confirmModal.variant}
        />
      )}
      <BulkUploadModal open={bulkUploadOpen} onClose={() => setBulkUploadOpen(false)} onUpload={handleBulkUpload} />

      <div className="w-full max-w-full px-2 sm:px-3 lg:px-2 py-2">
        {/* A draft just placed: the desks' booked ticket takes the page; the list stays mounted underneath for "Back to drafts". */}
        {placedTicket && (() => {
          const p = ticketPropsFor(placedTicket);
          return (
            <div className="px-1 sm:px-2">
              <BookedSuccess
                {...p}
                awbCopied={awbCopied}
                onCopyAwb={() => {
                  navigator.clipboard?.writeText(String(p.order.waybill));
                  setAwbCopied(true);
                  window.setTimeout(() => setAwbCopied(false), 1500);
                }}
                onViewDetails={() => p.order.orderRef && navigate(`/shipment-details/${encodeURIComponent(p.order.orderRef)}`)}
                onAllShipments={() => navigate("/all-shipments")}
                onBookAnother={() => setPlacedTicket(null)}
                bookAnotherLabel="Back to drafts"
                BookAnotherIcon={ArrowLeft}
              />
            </div>
          );
        })()}
        <div className={`bg-white rounded-xl shadow-sm ring-1 ring-slate-200/80 w-full max-w-full ${placedTicket ? "hidden" : ""}`}>

          {/* ===== Tabs: stick just under the fixed 64px app header (desktop only; the phone grid is 166px tall) ===== */}
          <div className="md:sticky md:top-16 z-20 rounded-t-xl bg-slate-50/95 backdrop-blur-sm border-b border-slate-200/80">
            <div className="md:hidden p-1">
              <div className="grid grid-cols-2 gap-1">
                {TABS.map((tab) => {
                  const active = selectedTab === tab.key;
                  const count = tabCount(tab.key);
                  return (
                    <button key={tab.key} type="button" onClick={() => handleTabClick(tab.key)} className={`relative flex flex-col items-center justify-center gap-1 rounded-lg px-2 py-1.5 font-medium transition-colors duration-150 ${active ? "bg-white shadow-sm text-slate-900 ring-1 ring-slate-200/80" : "text-slate-500 active:text-slate-800"}`}>
                      <div className="flex items-center gap-1">
                        <tab.Icon className={`h-3.5 w-3.5 flex-shrink-0 transition-colors ${active || count > 0 ? tab.color : "text-slate-400"}`} />
                        <span className="max-w-[90px] truncate text-[10px] font-semibold">{tab.label}</span>
                      </div>
                      <span className={`min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[10px] font-bold tabular-nums transition-colors ${tabBadgeClass(tab, count, active)}`}>{count}</span>
                      {active && <div className={`absolute inset-x-3 bottom-0 h-[2.5px] rounded-t-full ${tab.accent}`} />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="hidden md:block relative">
              {/* All tabs share the row equally, as on All Shipments - no horizontal overflow. */}
              <div>
                <div className="flex w-full p-1 gap-0.5">
                  {TABS.map((tab) => {
                    const active = selectedTab === tab.key;
                    const count = tabCount(tab.key);
                    return (
                      <button key={tab.key} type="button" onClick={() => handleTabClick(tab.key)} title={tab.label} className={`group relative flex flex-[1_0_auto] items-center justify-center gap-1 whitespace-nowrap rounded-lg px-2 py-1.5 font-medium transition-colors duration-150 ${active ? "bg-white shadow-sm text-slate-900 ring-1 ring-slate-200/80" : "text-slate-500 hover:text-slate-900"}`}>
                        <tab.Icon className={`hidden h-3.5 w-3.5 flex-shrink-0 transition-colors xl:block ${active || count > 0 ? tab.color : "text-slate-400 group-hover:text-slate-600"}`} />
                        <span className="text-xs font-semibold">{tab.label}</span>
                        <span className={`min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[10px] font-bold tabular-nums transition-colors ${tabBadgeClass(tab, count, active)}`}>{count}</span>
                        {active && <div className={`absolute inset-x-2.5 bottom-0 h-[2.5px] rounded-t-full ${tab.accent}`} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* ===== A bulk placement in flight or just finished ===== */}
          {run && (
            <PlacementRunCard
              run={run}
              onShow={showRunDrafts}
              onRetryFailed={retryRunFailures}
              retrying={bulkPlacing}
              onRefresh={refreshRun}
              onDismiss={() => { clearRunTimer(); setRun(null); }}
            />
          )}

          {/* ===== Toolbar (the All Shipments recipe): Filters left; bulk actions, Upload CSV and Refresh right ===== */}
          <div className="border-b border-gray-300 bg-white">
            <div className="p-1.5 md:p-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1.5">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`w-full sm:w-auto flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border text-xs font-semibold transition-colors ${showFilters
                  ? "border-slate-300 bg-slate-100 text-slate-900"
                  : "border-slate-200 bg-white text-slate-600 hover:text-slate-900"}`}
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-[#3877FC] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{activeFiltersCount}</span>
                )}
                <svg className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${showFilters ? "rotate-180" : ""}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              <div className="w-full sm:w-auto flex flex-wrap items-center gap-1">
                {/* Bulk actions read like All Shipments' CSV / Labels buttons: quiet until rows are selected, then tinted with a count. */}
                {/* Prices for the whole selection: one quote per distinct lane, then every row shows its courier menu. */}
                <button
                  type="button"
                  onClick={() => fetchCourierOptionsForDrafts(quotableSelected)}
                  disabled={quotableSelected.length === 0 || batchPricing}
                  aria-busy={batchPricing}
                  className={`${TOOL_BTN} ${quotableSelected.length === 0 || batchPricing ? TOOL_OFF : "border-[#3877FC]/30 bg-[#3877FC]/[0.06] text-[#2A59BD] hover:bg-[#3877FC]/[0.1]"}`}
                  title={quotableSelected.length === 0 ? "Select drafts to get courier prices" : "Fetch live courier prices for the selected drafts"}
                >
                  {batchPricing
                    ? <span className="h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-[#3877FC]/30 border-t-[#3877FC]" />
                    : <IndianRupee className="w-3.5 h-3.5 flex-shrink-0" />}
                  <span className="hidden lg:inline">Prices</span>
                  {quotableSelected.length > 0 && (
                    <span className="bg-[#3877FC] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{quotableSelected.length}</span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={openBulkPlace}
                  disabled={bookableSelected.length === 0 || bulkPlacing}
                  aria-busy={bulkPlacing}
                  className={`${TOOL_BTN} ${bookableSelected.length === 0 || bulkPlacing ? TOOL_OFF : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
                  title={bookableSelected.length === 0 ? "Select drafts to book" : "Book the selected drafts as shipments"}
                >
                  {bulkPlacing
                    ? <span className="h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-emerald-200 border-t-emerald-600" />
                    : <Send className="w-3.5 h-3.5 flex-shrink-0" />}
                  <span className="hidden lg:inline">Book</span>
                  {bookableSelected.length > 0 && (
                    <span className="bg-emerald-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{bookableSelected.length}</span>
                  )}
                </button>

                {/* One destructive action: Delete permanently removes the selected drafts (no soft-delete step). */}
                <button
                  type="button"
                  onClick={openBulkDelete}
                  disabled={permanentDeletableSelectedCount === 0}
                  className={`${TOOL_BTN} ${permanentDeletableSelectedCount === 0 ? TOOL_OFF : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"}`}
                  title={permanentDeletableSelectedCount === 0 ? "Select drafts to delete" : "Permanently delete the selected drafts"}
                >
                  <Trash2 className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="hidden lg:inline">Delete</span>
                  {permanentDeletableSelectedCount > 0 && (
                    <span className="bg-rose-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{permanentDeletableSelectedCount}</span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setBulkUploadOpen(true)}
                  className={`${TOOL_BTN} flex-1 sm:flex-none border-slate-200 bg-white text-slate-600 hover:text-slate-900`}
                  title="Create drafts from a CSV file"
                >
                  <Upload className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline text-xs">Upload CSV</span>
                </button>

                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={loading || refreshCooling}
                  title={refreshCooling ? `Refresh again in ${cooldownLeftS}s` : "Reload this list with the current filters"}
                  aria-label="Refresh drafts"
                  className={`${TOOL_BTN} relative overflow-hidden flex-1 sm:flex-none ${loading || refreshCooling ? "border-slate-200 bg-white text-slate-400 cursor-not-allowed" : "border-slate-200 bg-white text-slate-600 hover:text-slate-900"}`}
                >
                  <RefreshCw className={`h-3.5 w-3.5 flex-shrink-0 ${loading ? "animate-spin" : ""}`} />
                  <span className="hidden sm:inline text-xs">Refresh</span>
                  {refreshCooling && (
                    // drains right-to-left with the cooldown; empty = refresh is open again
                    <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[3px] bg-slate-100">
                      <span className="block h-full bg-[#3877FC]/60 transition-[width] duration-1000 ease-linear" style={{ width: `${Math.min(100, (cooldownLeftMs / REFRESH_COOLDOWN_MS) * 100)}%` }} />
                    </span>
                  )}
                </button>
                {/* The status caption All Shipments shows beside Refresh: what the last refresh changed, else how fresh the list is */}
                {refreshSummaryLabel ? (
                  <span className={`hidden lg:inline whitespace-nowrap pl-1 text-[11px] font-semibold tabular-nums ${refreshSummary.updated || refreshSummary.added ? "text-emerald-700" : "text-slate-500"}`} role="status">
                    {refreshSummaryLabel}
                  </span>
                ) : updatedAgo && (
                  <span className="hidden lg:inline whitespace-nowrap pl-1 text-[11px] font-medium text-slate-400 tabular-nums" title={lastFetchedAt ? `Last fetched ${new Date(lastFetchedAt).toLocaleString()}` : undefined}>
                    Updated {updatedAgo}
                  </span>
                )}
              </div>
            </div>

            {(activeFilterChips.length > 0 || runFilterId) && (
              <div className="px-3 pb-2 -mt-0.5 flex flex-wrap items-center gap-1.5">
                {runFilterId && (
                  <span className="inline-flex items-center gap-1 max-w-[18rem] pl-2.5 pr-1 py-1 rounded-full bg-amber-50 ring-1 ring-amber-200 text-xs text-amber-800" title="Only the drafts from this bulk booking">
                    <span className="font-semibold flex-shrink-0">Batch:</span>
                    <span className="truncate font-mono">{runFilterId}</span>
                    <button type="button" onClick={clearRunFilter} className="p-0.5 rounded-full text-amber-700/60 hover:text-rose-600 transition-colors flex-shrink-0" aria-label="Show all drafts again">
                      <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </button>
                  </span>
                )}
                {activeFilterChips.map((chip) => (
                  <span
                    key={chip.key}
                    className="inline-flex items-center gap-1 max-w-[16rem] pl-2.5 pr-1 py-1 rounded-full bg-[#3877FC]/[0.08] ring-1 ring-[#3877FC]/20 text-xs text-[#2A59BD]"
                  >
                    <span className="font-semibold flex-shrink-0">{chip.label}:</span>
                    <span className="truncate">{chip.value}</span>
                    <button
                      type="button"
                      onClick={() => removeFilterChip(chip.key)}
                      className="p-0.5 rounded-full text-[#2A59BD]/60 hover:text-rose-600 transition-colors flex-shrink-0"
                      aria-label={`Remove ${chip.label} filter`}
                    >
                      <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </button>
                  </span>
                ))}
                <button type="button" onClick={resetFilters} className="text-xs font-medium text-slate-400 hover:text-rose-600 transition-colors ml-1">
                  Clear all
                </button>
              </div>
            )}

            {showFilters && (
              <form
                className="px-4 py-4 bg-slate-50/60 border-t border-slate-200/80 animate-slide-down"
                onSubmit={(e) => { e.preventDefault(); handleFilterClick(); }}
              >
                {/* Only what the draft search API filters on: keyword, source, created date. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-4 mb-4">
                  <div className="group w-full">
                    <label htmlFor="df-keyword" className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-[#3877FC]"></span>
                      <span className="truncate">Keyword</span>
                    </label>
                    <input
                      id="df-keyword"
                      type="text"
                      name="searchKeyword"
                      value={filters.searchKeyword}
                      onChange={handleInputChange}
                      onBlur={(e) => setFilters((prev) => ({ ...prev, searchKeyword: e.target.value.trim() }))}
                      placeholder="Draft id, reference, customer, phone, tag…"
                      autoComplete="off"
                      className={`w-full h-9 px-3 text-sm text-slate-800 placeholder:text-slate-400 border rounded-lg focus:ring-2 focus:ring-[#3877FC]/25 focus:border-[#3877FC] outline-none transition-colors ${filters.searchKeyword
                        ? "border-[#3877FC]/40 bg-[#3877FC]/[0.03]"
                        : "border-slate-200 bg-white"}`}
                    />
                  </div>

                  <div className="group w-full">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-amber-500"></span>
                      <span>Source</span>
                    </label>
                    <SelectMenu
                      value={filters.orderSource}
                      onChange={(v) => setFilters((prev) => ({ ...prev, orderSource: v }))}
                      options={[{ value: "", label: "All sources" }, ...Object.values(DraftOrderSourceEnum).map((s) => ({ value: s.code, label: s.name }))]}
                    />
                  </div>

                  <div className="sm:col-span-1 w-full">
                    <div className="flex items-center gap-2 mb-1.5">
                      <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                        <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-amber-500"></span>
                        <span>Created Date</span>
                      </label>
                      {useDateFilter && (
                        <button
                          type="button"
                          onClick={() => setUseDateFilter(false)}
                          className="text-[10px] font-medium text-slate-400 hover:text-rose-600 transition-colors"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {useDateFilter ? (
                      <div className="date-input-wrapper w-full">
                        <CustomDateRangePicker
                          selectedRange={[parseISO(filters.fromDate), parseISO(filters.toDate)]}
                          onRangeChange={([from, to]) => {
                            const diffDays = Math.abs((to - from) / (1000 * 60 * 60 * 24));
                            if (diffDays > 90) {
                              toast.error("Date range cannot exceed 3 months");
                              return;
                            }
                            setFilters((prev) => ({
                              ...prev,
                              fromDate: from.toLocaleDateString("en-CA"),
                              toDate: to.toLocaleDateString("en-CA"),
                            }));
                          }}
                        />
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setUseDateFilter(true)}
                        className="w-full h-9 border border-dashed border-slate-300 rounded-lg text-xs text-slate-500 hover:text-[#2A59BD] transition-colors font-medium"
                      >
                        + Add Created Date Range
                      </button>
                    )}
                    <div className="mt-1.5 flex items-center gap-1">
                      {DATE_PRESETS.map(({ label, days }) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => applyDatePreset(days)}
                          title={days === 1 ? "Today" : `Last ${days} days`}
                          className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors ring-1 ${isPresetActive(days)
                            ? "bg-[#3877FC] text-white ring-[#3877FC]"
                            : "bg-[#3877FC]/[0.05] text-[#2A59BD] ring-[#3877FC]/20 hover:bg-[#3877FC]/10"}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row flex-wrap gap-2 items-stretch sm:items-center sm:justify-end pt-3 border-t border-slate-200/70">
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-3 py-1.5 bg-[#3877FC] hover:bg-[#2A59BD] text-white rounded-lg shadow-sm shadow-[#3877FC]/25 transition-colors font-semibold flex items-center justify-center gap-1.5 text-xs"
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>Apply Filters</span>
                  </button>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="w-full sm:w-auto px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg hover:text-slate-900 transition-colors font-semibold flex items-center justify-center gap-1.5 text-xs"
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Reset All</span>
                  </button>
                </div>
              </form>
            )}
          </div>
          {error && (
            <div className="mx-3 mt-3 flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700" role="alert">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* ===== Table (desktop) + cards (phone). One derivation per draft feeds both. ===== */}
          {loading ? <TableSkeleton /> : drafts.length === 0 ? <EmptyState tab={selectedTab} onReset={resetFilters} hasFilters={activeFiltersCount > 0 || !useDateFilter} /> : (
            <>
              {/* While the table fits, the wrapper stays overflow-visible so the column headers can stick under the tab
                  strip (64px header + 40px strip). Once it overflows, it becomes the horizontal scroller and the
                  headers scroll with it, as on All Shipments. */}
              <div ref={scrollRef} className={`hidden md:block w-full max-w-full ${hasHScroll ? "overflow-x-auto" : "overflow-visible"}`}>
                <table className="w-full table-auto">
                  <thead className={`bg-slate-50 ${hasHScroll ? "" : "sticky top-[104px] z-10"}`}>
                    <tr>
                      <th className="px-2.5 py-2 w-10 shadow-[inset_0_-1px_0_#e2e8f0]">
                        <div className="flex items-center justify-center">
                          <input ref={headerCheckboxRef} type="checkbox" checked={allOnPageSelected} onChange={(e) => toggleSelectAllOnPage(e.target.checked)} aria-label="Select all drafts on this page" className="h-4 w-4 accent-[#3877FC] cursor-pointer rounded" />
                        </div>
                      </th>
                      <th className={TH}>Draft</th>
                      <th className={TH}>Route</th>
                      <th className={TH}>Value</th>
                      <th className={TH}>Package</th>
                      <th className={TH}>Status</th>
                      <th className={TH}>Courier</th>
                      <th className={`py-2 px-1 w-[92px] text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap shadow-[inset_0_-1px_0_#e2e8f0] ${hasHScroll ? "sticky right-0 bg-slate-50" : ""}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {drafts.map((d) => {
                      const r = deriveRow(d);
                      const id = d.draft_order_id;
                      const checked = selectedRows.has(id);
                      const isPlacing = placingIds.has(id);
                      const isRetrying = retryingIds.has(id);
                      const rowBusy = isPlacing || isRetrying;
                      const missing = draftValidationResults[id] || [];
                      const canPlaceNow = d.draft_status === "DRAFT" && missing.length === 0;
                      const expanded = Boolean(expandedErrorLogs[id]);
                      const isEligible = d.draft_status === "DRAFT" || d.draft_status === "FAILED";
                      const loadingCourier = loadingCarrierKeys.has(id);
                      const courierOptions = carrierOptionsByKey[id] || [];
                      // A placed draft shows the courier that actually booked it (smart allocation resolves server-side).
                      const chosenCarrier = carrierSelectionByKey[id] ?? r.carrierName ?? d.placed_order_summary?.carrier_partner_public_name ?? null;
                      const quoteBlockers = isEligible ? quoteBlockersOf(r.od) : [];
                      const summaryAwb = d.placed_order_summary?.waybill || null;
                      // A booked draft's shipment opens by the order reference it was booked under (never the internal shipment id).
                      const shipmentRef = d.draft_status === "PLACED" ? (d.placed_order_summary?.order_reference_number || r.orderRef || null) : null;
                      const beingPlaced = d.draft_status === "QUEUED" && d.placing === true;
                      const cheapest = courierOptions.length ? courierOptions.reduce((m, o) => (o.price < m.price ? o : m), courierOptions[0]) : null;
                      const firstItem = r.allItems[0];
                      const firstName = firstItem?.item_general_details?.item_name || firstItem?.item_general_details?.sku || null;
                      const firstQty = Number(firstItem?.quantity);
                      const remaining = r.allItems.length - 1;
                      const countLabel = [firstQty > 1 ? `×${firstQty}` : null, remaining > 0 ? `+${remaining} more` : null].filter(Boolean).join(" · ") || "1 item";
                      const failureText = r.hasError ? placementErrorMessage(d.placement_error.error_message) : null;

                      return (
                        <React.Fragment key={id}>
                          <tr
                            onClick={(e) => { if (e.target.closest("[data-row-noclick], button, a, input, label, select, summary")) return; toggleRow(id); }}
                            className={`group relative transition-colors cursor-pointer text-sm ${checked ? "bg-[#3877FC]/[0.07]" : "hover:bg-[#3877FC]/[0.05]"} ${rowBusy ? "opacity-60" : ""}`}
                            aria-busy={rowBusy || undefined}
                          >
                            {/* Row checkbox + status-tinted scan edge */}
                            <td data-row-noclick="" className="px-2.5 py-2.5 w-10 relative align-top cursor-default">
                              <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-[3px] ${r.tone.edge}`} />
                              <div className="flex items-center justify-center">
                                <input type="checkbox" checked={checked} onChange={() => toggleSelectOne(id)} className="h-4 w-4 accent-[#3877FC] cursor-pointer rounded" title={checked ? "Unselect" : "Select"} aria-label={`Select ${r.orderRef || id}`} />
                              </div>
                            </td>

                            {/* Draft: reference is the identity, draft id its shadow, then title and created */}
                            <td className={`${TD} min-w-[190px]`}>
                              <div className="flex flex-col gap-0.5">
                                <span className="flex items-baseline gap-1 min-w-0">
                                  <span className={`${EYEBROW} text-violet-500`}>Ref</span>
                                  <span className="font-mono text-[12px] font-semibold text-slate-900 break-all leading-tight min-w-0">
                                    {r.orderRef || <span className="font-sans font-normal text-slate-400">No reference</span>}
                                    {r.orderRef && <CellCopy value={r.orderRef} title="Copy order reference" />}
                                  </span>
                                </span>
                                <span className="flex items-baseline gap-1 min-w-0">
                                  <span className={`${EYEBROW} text-[#3877FC]`}>Id</span>
                                  <span className="font-mono text-[11px] text-slate-500 break-all leading-tight min-w-0">
                                    {id}
                                    <CellCopy value={id} title="Copy draft id" />
                                  </span>
                                </span>
                                {draftTitleOf(d) && (
                                  <span className="flex items-center gap-1 min-w-0">
                                    <span className="w-7 flex-shrink-0" />
                                    <span className="text-[11px] text-slate-600 truncate" title={draftTitleOf(d)}>{draftTitleOf(d)}</span>
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <span className="w-7 flex-shrink-0" />
                                  <span className="text-[11px] text-slate-500 whitespace-nowrap">
                                    {formatShortIST(d.created_date_time)}
                                    {relTime(d.created_date_time) && <span className="text-slate-400"> ({relTime(d.created_date_time)})</span>}
                                  </span>
                                </span>
                              </div>
                            </td>

                            {/* Route: the All Shipments cell, dots inside their city rows */}
                            <td className={`${TD} min-w-[180px] max-w-[240px]`}>
                              <div className="flex flex-col gap-0.5 min-w-0">
                                <div className="flex flex-col min-w-0" title={`From: ${r.pickup.pickup_organisation || r.pickup.pickup_poc || "—"} · ${cityPin(r.pickup.pickup_city, r.pickup.pickup_postal_code) || "—"}`}>
                                  <span className="flex items-center gap-1.5 min-w-0">
                                    <span aria-hidden="true" className="h-2 w-2 rounded-full bg-amber-500 ring-2 ring-amber-100 flex-shrink-0" />
                                    <span className="flex items-baseline gap-1 min-w-0">
                                      <span className="text-[13px] font-medium text-slate-900 truncate min-w-0">{titleCaseName(r.pickup.pickup_city) || "—"}</span>
                                      {r.pickup.pickup_postal_code && (
                                        <span className="flex-shrink-0 inline-flex items-center text-xs text-slate-500 tabular-nums">
                                          {r.pickup.pickup_postal_code}
                                          <CellCopy value={r.pickup.pickup_postal_code} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100" />
                                        </span>
                                      )}
                                    </span>
                                  </span>
                                  <span className="pl-[14px] text-[11px] text-slate-500 truncate">{titleCaseName(r.pickup.pickup_organisation || r.pickup.pickup_poc) || "—"}</span>
                                </div>
                                <span aria-hidden="true" className="ml-[3.5px] h-2.5 w-px bg-slate-200 flex-shrink-0" />
                                <div className="flex flex-col min-w-0" title={`To: ${r.drop.drop_poc || "—"} · ${cityPin(r.drop.drop_city, r.drop.drop_postal_code) || "—"}`}>
                                  <span className="flex items-center gap-1.5 min-w-0">
                                    <span aria-hidden="true" className="h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-emerald-100 flex-shrink-0" />
                                    <span className="flex items-baseline gap-1 min-w-0">
                                      <span className="text-[13px] font-medium text-slate-900 truncate min-w-0">{titleCaseName(r.drop.drop_city) || "—"}</span>
                                      {r.drop.drop_postal_code && (
                                        <span className="flex-shrink-0 inline-flex items-center text-xs text-slate-500 tabular-nums">
                                          {r.drop.drop_postal_code}
                                          <CellCopy value={r.drop.drop_postal_code} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100" />
                                        </span>
                                      )}
                                    </span>
                                  </span>
                                  <span className="pl-[14px] text-[11px] text-slate-500 truncate">{titleCaseName(r.drop.drop_poc) || <span className="text-slate-400">{r.isReverse ? "Return location missing" : "Customer name missing"}</span>}</span>
                                </div>
                              </div>
                            </td>

                            {/* Value */}
                            <td className={TD}>
                              <div className="flex flex-col items-start gap-1">
                                <span className="text-[13px] font-semibold text-slate-900 tabular-nums whitespace-nowrap" title={r.paymentType === "COD" ? "COD - to collect from customer" : "Declared invoice value"}>
                                  {r.amount > 0 ? fmtInr(r.amount, 2) : <span className="font-normal text-slate-400">—</span>}
                                </span>
                                {r.paymentType ? (
                                  <>
                                    <span className={`${TYPE_CHIP} ${r.paymentType === "COD" ? "bg-amber-50 ring-amber-200 text-amber-700" : "bg-emerald-50 ring-emerald-200 text-emerald-700"}`}>{r.paymentType}</span>
                                    {r.paymentType === "COD" && <span className="text-[10px] text-slate-400 whitespace-nowrap">to collect</span>}
                                  </>
                                ) : <span className="text-xs text-slate-400">—</span>}
                              </div>
                            </td>

                            {/* Package: first item + count, then weight and dimensions */}
                            <td className={`${TD} min-w-[150px] max-w-[190px]`}>
                              <div className="flex flex-col gap-0.5 min-w-0">
                                {r.allItems.length > 0 ? (
                                  <>
                                    <span className="text-xs text-slate-700 truncate" title={r.allItems.map((it) => `${it.item_general_details?.item_name || it.item_general_details?.sku || "Item"} × ${it.quantity || 1}`).join(", ")}>{firstName || "Item"}</span>
                                    <span className="text-[10px] text-slate-500">{countLabel}</span>
                                  </>
                                ) : <span className="text-xs text-slate-400">No items</span>}
                                {(r.weight || r.dims) && (
                                  <span className="flex items-baseline gap-1 tabular-nums whitespace-nowrap min-w-0">
                                    {r.weight && <><span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Wt</span><span className="text-xs text-slate-600">{r.weight}</span></>}
                                    {r.dims && <span className="truncate text-[11px] text-slate-400" title={r.dims}>{r.weight ? "· " : ""}{r.dims}</span>}
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Status: chip, readiness, when; failure line + details toggle */}
                            <td className={`${TD} max-w-[230px]`}>
                              <div className="flex flex-col gap-0.5 min-w-0">
                                <div className="flex flex-wrap items-center gap-1">
                                  <DraftStatusBadge status={d.draft_status} />
                                  {isEligible && (
                                    missing.length === 0 ? (
                                      d.draft_status === "DRAFT" && <span className={`${TYPE_CHIP} normal-case tracking-normal bg-emerald-50 ring-emerald-200 text-emerald-700`}>Ready to book</span>
                                    ) : (
                                      <div className="group/val relative inline-block" data-row-noclick="">
                                        <span tabIndex={0} className={`${TYPE_CHIP} normal-case tracking-normal cursor-help bg-amber-50 ring-amber-200 text-amber-700`}>{missing.length} field{missing.length > 1 ? "s" : ""} missing</span>
                                        <div className="invisible absolute left-0 top-full z-50 mt-1.5 w-64 whitespace-normal rounded-xl bg-white p-3 opacity-0 shadow-xl ring-1 ring-slate-200 transition-opacity group-hover/val:visible group-hover/val:opacity-100 group-focus-within/val:visible group-focus-within/val:opacity-100">
                                          <p className={MICRO}>Needed before booking</p>
                                          <ul className="mt-1.5 space-y-1">
                                            {missing.map((err, ei) => (
                                              <li key={ei} className="flex items-start gap-1.5 text-xs text-slate-700">
                                                <span className="mt-[6px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400" />
                                                <span><span className="text-slate-400">{err.section} · </span>{err.field}</span>
                                              </li>
                                            ))}
                                          </ul>
                                          <button type="button" onClick={() => goToDeskWith(d)} className="mt-2.5 text-xs font-medium text-[#2A59BD] hover:underline underline-offset-2">Edit the draft</button>
                                        </div>
                                      </div>
                                    )
                                  )}
                                </div>
                                <span className="text-[11px] text-slate-500 whitespace-nowrap">
                                  {isPlacing || beingPlaced ? <span className="inline-flex items-center gap-1 text-[#2A59BD]"><span className="h-3 w-3 animate-spin rounded-full border-2 border-[#3877FC]/30 border-t-[#3877FC]" /> {beingPlaced ? "Being booked…" : "Booking…"}</span>
                                    : isRetrying ? <span className="inline-flex items-center gap-1 text-amber-700"><span className="h-3 w-3 animate-spin rounded-full border-2 border-amber-300 border-t-amber-600" /> Retrying…</span>
                                      : r.stampIso ? <>{r.tone.verb} {formatShortIST(r.stampIso)}{relTime(r.stampIso) && <span className="text-slate-400"> ({relTime(r.stampIso)})</span>}</> : null}
                                </span>
                                {/* Booked: the AWB (the revamped OMS keeps it on the draft) with copy and tracking */}
                                {d.draft_status === "PLACED" && summaryAwb && (
                                  <span className="flex items-center gap-0.5 min-w-0">
                                    <span className="text-[9px] font-bold uppercase tracking-wider text-[#3877FC] mr-0.5">AWB</span>
                                    <span className="font-mono text-[12px] font-semibold text-slate-900 truncate" title={summaryAwb}>{summaryAwb}</span>
                                    <CellCopy value={summaryAwb} title="Copy AWB" />
                                    <a href={trackingUrl(summaryAwb)} target="_blank" rel="noopener noreferrer" data-row-noclick="" className="ml-1 text-[11px] font-medium text-[#2A59BD] hover:text-[#1C3B7E] hover:underline underline-offset-2 whitespace-nowrap" title="Track this shipment">Track</a>
                                  </span>
                                )}
                                {/* Booked before the revamp (no AWB on the draft): the Shipment button opens it by reference; the
                                    internal shipment id means nothing to a merchant, so it is not shown. */}
                                {r.hasError && (
                                  <>
                                    <span className="text-[11px] text-rose-600 leading-snug truncate" title={failureText}>{failureText}</span>
                                    <button type="button" data-row-noclick="" onClick={() => toggleRow(id)} aria-expanded={expanded} className="inline-flex w-fit items-center gap-0.5 text-[11px] font-medium text-rose-700 hover:underline underline-offset-2">
                                      {expanded ? "Hide details" : "Why it failed"}
                                      <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>

                            {/* Courier: who carries it (the ticket's Courier fact), type / source chips, then prices */}
                            <td className={`${TD} min-w-[180px] max-w-[230px]`} data-carrier-dropdown-root={id}>
                              <div className="flex flex-col gap-1.5 min-w-0">
                                <CourierIdentity name={chosenCarrier} />
                                <span className="flex flex-wrap items-center gap-1 min-w-0">
                                  <span className={`${TYPE_CHIP} ${r.isReverse ? "bg-purple-50 ring-purple-200 text-purple-700" : "bg-sky-50 ring-sky-200 text-sky-700"}`}>{r.isReverse ? "REVERSE" : "FORWARD"}</span>
                                  <SourceBadge source={d.order_source} />
                                </span>
                                {isEligible && (loadingCourier ? (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" /> Fetching prices…</span>
                                ) : courierOptions.length > 0 ? (
                                  <div className="relative w-full" data-row-noclick="">
                                    <button
                                      ref={carrierDropdownOpenKey === id ? carrierTriggerRef : undefined}
                                      type="button"
                                      onClick={() => setCarrierDropdownOpenKey((prev) => (prev === id ? null : id))}
                                      className={`flex h-8 w-full items-center justify-between gap-1.5 rounded-lg border bg-white pl-2.5 pr-1.5 text-left text-[11.5px] font-medium transition-colors ${carrierDropdownOpenKey === id ? "border-[#3877FC]/50 text-[#2A59BD]" : "border-slate-200 text-slate-700 hover:border-[#3877FC]/40 hover:text-[#2A59BD]"}`}
                                      title="Choose the courier for this draft"
                                    >
                                      <span className="min-w-0 truncate">
                                        {(() => { const sel = courierOptions.find((c) => c.name === chosenCarrier); return sel ? `${formatCarrier(sel.name).carrier} · ${fmtMoney(sel.price)}` : cheapest ? `Choose courier · from ${fmtMoney(cheapest.price)}` : "Choose courier"; })()}
                                      </span>
                                      <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 text-slate-400 transition-transform ${carrierDropdownOpenKey === id ? "rotate-180" : ""}`} />
                                    </button>
                                    {carrierDropdownOpenKey === id && ReactDOM.createPortal(
                                      <CourierMenu options={courierOptions} chosen={chosenCarrier} onPick={(name) => handleCourierSelect(id, name)} position={carrierDropdownPosition} menuRef={carrierMenuRef} />,
                                      document.body,
                                    )}
                                  </div>
                                ) : quoteBlockers.length === 0 ? (
                                  <button type="button" data-row-noclick="" onClick={() => performCourierFetchForDraft(d)} className="inline-flex w-fit items-center gap-0.5 text-[11px] font-medium text-[#2A59BD] hover:text-[#1C3B7E] hover:underline underline-offset-2" title="Fetch live courier prices for this draft">
                                    Get prices
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M7 7h10v10" /></svg>
                                  </button>
                                ) : (
                                  <span data-row-noclick="" className="inline-flex w-fit cursor-not-allowed items-center gap-1 text-[11px] font-medium text-slate-400" title={quoteBlockersLabel(quoteBlockers)}>
                                    Get prices <span className="font-normal">· needs {quoteBlockers.length === 1 ? quoteBlockers[0] : `${quoteBlockers.length} details`}</span>
                                  </span>
                                ))}
                              </div>
                            </td>

                            {/* Actions: one labelled action per state, then expand + menu */}
                            <td data-row-noclick="" className={`py-2.5 px-1 align-top cursor-default w-[92px] ${hasHScroll ? `sticky right-0 shadow-[inset_1px_0_0_#e2e8f0] ${checked ? "bg-[#F1F5FF]" : "bg-white group-hover:bg-[#F5F8FF]"}` : ""}`}>
                              <div className="flex flex-col items-center gap-1">
                                {d.draft_status === "DRAFT" && (
                                  <button type="button" onClick={() => handleActionClick("place", d)} disabled={!canPlaceNow || isPlacing} className={`w-full ${canPlaceNow && !isPlacing ? ROW_BTN_ACCENT : ROW_BTN_OFF}`} title={canPlaceNow ? "Book this draft as a shipment" : `${missing.length} field${missing.length > 1 ? "s" : ""} missing`}>
                                    <Send className="h-3.5 w-3.5" /> Book
                                  </button>
                                )}
                                {d.draft_status === "FAILED" && (
                                  <button type="button" onClick={() => handleRetryPlace(d)} disabled={isRetrying || !d.placement_error?.retryable} className={`w-full ${!isRetrying && d.placement_error?.retryable ? ROW_BTN_AMBER : ROW_BTN_OFF}`} title={d.placement_error?.retryable ? "Retry booking this draft" : "The courier will not take this order as it is. Edit the draft, then book it again."}>
                                    <RotateCcw className={`h-3.5 w-3.5 ${isRetrying ? "animate-spin" : ""}`} /> Retry
                                  </button>
                                )}
                                {shipmentRef && (
                                  <button type="button" onClick={() => handleActionClick("viewShipment", d)} className={`w-full ${ROW_BTN} text-slate-700 bg-white border-slate-200 hover:bg-slate-50`} title={`Open shipment ${shipmentRef}`}>
                                    <ExternalLink className="h-3.5 w-3.5" /> Shipment
                                  </button>
                                )}
                                <div className="flex items-center gap-1">
                                  <button type="button" onClick={() => toggleRow(id)} aria-expanded={expanded} title={expanded ? "Hide draft details" : "Show draft details"} className={SQUARE_BTN}>
                                    <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${expanded ? "rotate-180" : ""}`} />
                                  </button>
                                  <ActionMenu draft={d} onAction={handleActionClick} onOpenChange={() => {}} validationErrors={missing} />
                                </div>
                              </div>
                            </td>
                          </tr>

                          {expanded && (
                            <tr>
                              <td colSpan={8} className="p-0 bg-white">
                                <DraftDetailsPanel draft={d} row={r} missing={missing} retrying={isRetrying} onEdit={() => goToDeskWith(d)} onRetry={() => handleRetryPlace(d)} onOpenShipment={() => handleActionClick("viewShipment", d)} onCollapse={() => toggleRow(id)} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Phone: the same facts as a card list, controls always visible */}
              <div className="md:hidden divide-y divide-slate-100">
                {drafts.map((d) => {
                  const r = deriveRow(d);
                  const id = d.draft_order_id;
                  const checked = selectedRows.has(id);
                  const isPlacing = placingIds.has(id);
                  const isRetrying = retryingIds.has(id);
                  const missing = draftValidationResults[id] || [];
                  const canPlaceNow = d.draft_status === "DRAFT" && missing.length === 0;
                  const expanded = Boolean(expandedErrorLogs[id]);
                  const failureText = r.hasError ? placementErrorMessage(d.placement_error.error_message) : null;
                  const summaryAwb = d.placed_order_summary?.waybill || null;
                  const shipmentRef = d.draft_status === "PLACED" ? (d.placed_order_summary?.order_reference_number || r.orderRef || null) : null;
                  const beingPlaced = d.draft_status === "QUEUED" && d.placing === true;
                  const hasPrimary = d.draft_status === "DRAFT" || d.draft_status === "FAILED" || Boolean(shipmentRef);
                  return (
                    <div key={id} className={`relative px-3 py-3 ${checked ? "bg-[#3877FC]/[0.07]" : ""} ${isPlacing || isRetrying ? "opacity-60" : ""}`}>
                      <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-[3px] ${r.tone.edge}`} />
                      <div className="flex items-start gap-2.5">
                        <input type="checkbox" checked={checked} onChange={() => toggleSelectOne(id)} className="mt-0.5 h-4 w-4 accent-[#3877FC] cursor-pointer rounded flex-shrink-0" aria-label={`Select ${r.orderRef || id}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex flex-col gap-0.5">
                              <span className="flex items-baseline gap-1 min-w-0">
                                <span className={`${EYEBROW} text-violet-500`}>Ref</span>
                                <span className="font-mono text-[12px] font-semibold text-slate-900 break-all leading-tight min-w-0">{r.orderRef || <span className="font-sans font-normal text-slate-400">No reference</span>}{r.orderRef && <CellCopy value={r.orderRef} title="Copy order reference" />}</span>
                              </span>
                              <span className="flex items-baseline gap-1 min-w-0">
                                <span className={`${EYEBROW} text-[#3877FC]`}>Id</span>
                                <span className="font-mono text-[11px] text-slate-500 break-all leading-tight min-w-0">{id}<CellCopy value={id} title="Copy draft id" /></span>
                              </span>
                              {draftTitleOf(d) && <span className="pl-8 text-[11px] text-slate-600 truncate">{draftTitleOf(d)}</span>}
                            </div>
                            <DraftStatusBadge status={d.draft_status} />
                          </div>
                          <div className="mt-2 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
                            <div className="min-w-0 flex flex-col gap-0.5">
                              <span className="flex items-center gap-1.5 min-w-0"><span aria-hidden="true" className="h-2 w-2 rounded-full bg-amber-500 ring-2 ring-amber-100 flex-shrink-0" /><span className="text-[12px] font-medium text-slate-900 truncate">{titleCaseName(r.pickup.pickup_city) || "—"}</span><span className="text-[11px] text-slate-500 tabular-nums">{r.pickup.pickup_postal_code}</span></span>
                              <span className="pl-[14px] text-[11px] text-slate-500 truncate">{titleCaseName(r.pickup.pickup_organisation || r.pickup.pickup_poc) || "—"}</span>
                              <span className="flex items-center gap-1.5 min-w-0"><span aria-hidden="true" className="h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-emerald-100 flex-shrink-0" /><span className="text-[12px] font-medium text-slate-900 truncate">{titleCaseName(r.drop.drop_city) || "—"}</span><span className="text-[11px] text-slate-500 tabular-nums">{r.drop.drop_postal_code}</span></span>
                              <span className="pl-[14px] text-[11px] text-slate-500 truncate">{titleCaseName(r.drop.drop_poc) || <span className="text-slate-400">{r.isReverse ? "Return location missing" : "Customer name missing"}</span>}</span>
                            </div>
                            <div className="flex flex-col items-end gap-0.5 text-right">
                              <span className="text-[13px] font-semibold text-slate-900 tabular-nums">{r.amount > 0 ? fmtInr(r.amount, 2) : "—"}</span>
                              {r.paymentType && <span className={`${TYPE_CHIP} ${r.paymentType === "COD" ? "bg-amber-50 ring-amber-200 text-amber-700" : "bg-emerald-50 ring-emerald-200 text-emerald-700"}`}>{r.paymentType}</span>}
                              <span className="text-[11px] text-slate-500 truncate max-w-[140px]">{r.allItems.length ? `${r.itemCount} item${r.itemCount === 1 ? "" : "s"}` : "No items"}</span>
                              {(r.weight || r.dims) && <span className="text-[11px] text-slate-400 tabular-nums truncate max-w-[140px]">{[r.weight, r.dims].filter(Boolean).join(" · ")}</span>}
                            </div>
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1">
                            {d.draft_status === "DRAFT" && missing.length === 0 && <span className={`${TYPE_CHIP} normal-case tracking-normal bg-emerald-50 ring-emerald-200 text-emerald-700`}>Ready to book</span>}
                            {(d.draft_status === "DRAFT" || d.draft_status === "FAILED") && missing.length > 0 && <span className={`${TYPE_CHIP} normal-case tracking-normal bg-amber-50 ring-amber-200 text-amber-700`}>{missing.length} field{missing.length > 1 ? "s" : ""} missing</span>}
                            <span className={`${TYPE_CHIP} ${r.isReverse ? "bg-purple-50 ring-purple-200 text-purple-700" : "bg-sky-50 ring-sky-200 text-sky-700"}`}>{r.isReverse ? "REVERSE" : "FORWARD"}</span>
                            <SourceBadge source={d.order_source} />
                            <span className="text-[11px] text-slate-500 ml-auto whitespace-nowrap">{beingPlaced ? "Being booked…" : r.stampIso ? `${r.tone.verb} ${relTime(r.stampIso) || formatShortIST(r.stampIso)}` : null}</span>
                          </div>
                          {r.hasError && <p className="mt-1 text-[11px] text-rose-600 leading-snug">{failureText}</p>}
                          {summaryAwb && (
                            <p className="mt-1 flex items-center gap-1 min-w-0">
                              <span className="text-[9px] font-bold uppercase tracking-wider text-[#3877FC]">AWB</span>
                              <span className="font-mono text-[12px] font-semibold text-slate-900 truncate">{summaryAwb}</span>
                              <CellCopy value={summaryAwb} title="Copy AWB" className="p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors" />
                              <a href={trackingUrl(summaryAwb)} target="_blank" rel="noopener noreferrer" className="ml-1 text-[11px] font-medium text-[#2A59BD] whitespace-nowrap">Track</a>
                            </p>
                          )}
                          <div className="mt-2 flex items-center gap-1.5">
                            {d.draft_status === "DRAFT" && <button type="button" onClick={() => handleActionClick("place", d)} disabled={!canPlaceNow || isPlacing} className={`flex-1 ${canPlaceNow && !isPlacing ? ROW_BTN_ACCENT : ROW_BTN_OFF}`}><Send className="h-3.5 w-3.5" /> Book</button>}
                            {d.draft_status === "FAILED" && <button type="button" onClick={() => handleRetryPlace(d)} disabled={isRetrying || !d.placement_error?.retryable} className={`flex-1 ${!isRetrying && d.placement_error?.retryable ? ROW_BTN_AMBER : ROW_BTN_OFF}`} title={d.placement_error?.retryable ? "Retry booking this draft" : "Edit the draft, then book it again"}><RotateCcw className={`h-3.5 w-3.5 ${isRetrying ? "animate-spin" : ""}`} /> Retry</button>}
                            {shipmentRef && <button type="button" onClick={() => handleActionClick("viewShipment", d)} className={`flex-1 ${ROW_BTN} text-slate-700 bg-white border-slate-200 hover:bg-slate-50`}><ExternalLink className="h-3.5 w-3.5" /> Shipment</button>}
                            {!hasPrimary && <span className="flex-1" />}
                            <button type="button" onClick={() => toggleRow(id)} aria-expanded={expanded} title={expanded ? "Hide draft details" : "Show draft details"} className={SQUARE_BTN}><ChevronDown className={`h-4 w-4 transition-transform duration-150 ${expanded ? "rotate-180" : ""}`} /></button>
                            <ActionMenu draft={d} onAction={handleActionClick} onOpenChange={() => {}} validationErrors={missing} />
                          </div>
                        </div>
                      </div>
                      {expanded && (
                        <div className="-mx-3 mt-2">
                          <DraftDetailsPanel draft={d} row={r} missing={missing} retrying={isRetrying} onEdit={() => goToDeskWith(d)} onRetry={() => handleRetryPlace(d)} onOpenShipment={() => handleActionClick("viewShipment", d)} onCollapse={() => toggleRow(id)} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ===== Pagination (All Shipments recipe) ===== */}
          {!loading && drafts.length > 0 && (() => {
            const rangeStart = (currentPage - 1) * limit + 1;
            const rangeEnd = Math.min(currentPage * limit, Math.max(totalElements, rangeStart + drafts.length - 1));
            return (
              <div className="border-t border-slate-200/80 bg-white px-3 md:px-4 py-2.5 rounded-b-xl">
                <div className="flex flex-wrap justify-between items-center gap-3">
                  <span className="text-sm text-slate-500 tabular-nums">
                    <span className="font-semibold text-slate-800">{rangeStart.toLocaleString("en-IN")}–{rangeEnd.toLocaleString("en-IN")}</span>
                    {totalElements > 0 && (
                      <>
                        <span className="mx-1">of</span>
                        <span className="font-semibold text-slate-800">{totalElements.toLocaleString("en-IN")}</span>
                      </>
                    )}
                    <span className="ml-1">drafts</span>
                  </span>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Rows per page</span>
                      <div className="w-20">
                        <SelectMenu value={limit} onChange={(v) => handleLimitChange({ target: { value: v } })} buttonClassName="h-8 text-sm font-semibold" dropUp options={[20, 50, 100].map((n) => ({ value: n, label: String(n) }))} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button type="button" onClick={prevPage} disabled={currentPage === 1} title="Previous page" className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-slate-900 disabled:text-slate-300 disabled:cursor-not-allowed transition-colors">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-slate-500 tabular-nums whitespace-nowrap px-1">
                        Page <span className="font-semibold text-slate-800">{currentPage}</span>
                        {totalPages > 1 && <> of <span className="font-semibold text-slate-800">{totalPages}</span></>}
                      </span>
                      <button type="button" onClick={nextPage} disabled={drafts.length < limit || currentPage >= totalPages} title="Next page" className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-slate-900 disabled:text-slate-300 disabled:cursor-not-allowed transition-colors">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
